/** English UI strings. zh-TW.ts must have the same keys (checked by the type and a unit test). */
export const en = {
  common: {
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    loading: 'Loading…',
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    saved: 'Saved',
    create: 'Create',
    edit: 'Edit',
    rename: 'Rename',
    remove: 'Remove',
    archive: 'Archive',
    unarchive: 'Unarchive',
    archived: 'Archived',
    actions: 'Actions',
    optional: 'Optional',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Copy failed',
    loadMore: 'Load more',
    backHome: 'Back to the dashboard',
  },
  nav: {
    primary: 'Main navigation',
    skipToContent: 'Skip to content',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    dashboard: 'Dashboard',
    findings: 'Findings',
    clients: 'Clients & projects',
    eol: 'End-of-life notices',
    agents: 'Coding agents',
    workspaceSection: 'Workspace',
    members: 'Members',
    settings: 'Settings & SLA',
    notifications: 'Notifications',
    apiTokens: 'API tokens',
    audit: 'Audit log',
    reports: 'Reports',
    github: 'GitHub',
    billing: 'Billing',
    plans: 'Plans',
    staffConsole: 'Staff console',
    workspace: 'Workspace',
    chooseWorkspace: 'Choose a workspace',
    createWorkspace: '+ New workspace',
    noWorkspace: 'Create or choose a workspace to start.',
    language: 'Language',
    userMenu: 'Account menu',
    help: 'Help',
    dataSources: 'Data sources and licenses',
    helpTitle: 'Help (opens the user guide in a new tab)',
    account: 'Account settings',
    signOut: 'Sign out',
  },
  legal: {
    terms: 'Terms of service',
    privacy: 'Privacy policy',
    refund: 'Refund policy',
  },
  theme: {
    toggle: 'Theme: {{mode}} (click to change)',
    light: 'light',
    dark: 'dark',
    system: 'system',
  },
  disclaimer: 'Fixes are suggestions. Review the changes and run the tests before deploying.',
  validation: {
    required: 'Required',
    email: 'Enter a valid email address',
    passwordMin: 'Use at least 12 characters',
    passwordMax: 'Use at most 256 characters',
    passwordMismatch: 'The passwords do not match',
  },
  errors: {
    title: 'Something went wrong',
    unexpected: 'An unexpected error occurred. Try again, or go back to the dashboard.',
    notFoundTitle: 'Not found',
    notFoundBody: 'This page does not exist, or you do not have access to it.',
    NETWORK: 'Cannot reach Smoke. Check your connection and try again.',
    UNKNOWN: 'Something went wrong ({{code}}). Try again.',
    VALIDATION_FAILED: 'Some fields are not valid. Check them and try again.',
    UNAUTHENTICATED: 'Your session has ended. Sign in again.',
    CSRF_TOKEN_INVALID: 'Your session token expired. Reload the page and try again.',
    FORBIDDEN: 'You do not have permission to do this.',
    NOT_FOUND: 'Not found.',
    CONFLICT: 'This conflicts with the current state. Reload and try again.',
    RATE_LIMITED: 'Too many attempts. Wait a moment and try again.',
    PAYLOAD_TOO_LARGE: 'The request is too large.',
    UNSUPPORTED_MEDIA_TYPE: 'This kind of content is not accepted here.',
    NOT_IMPLEMENTED: 'This is not available yet.',
    INTERNAL_ERROR: 'Smoke had a problem. Try again in a moment.',
    AUTH_INVALID_CREDENTIALS: 'The email or password is not correct.',
    AUTH_EMAIL_NOT_VERIFIED: 'Verify your email address first.',
    AUTH_TOKEN_INVALID: 'This link is invalid or has expired.',
    AUTH_MFA_PENDING: 'Enter your two-factor code to continue.',
    AUTH_MFA_INVALID_CODE: 'The code is not correct.',
    AUTH_MFA_ALREADY_ENABLED: 'Two-factor authentication is already on.',
    AUTH_MFA_NOT_ENABLED: 'Two-factor authentication is not on.',
    AUTH_MFA_ENROLLMENT_REQUIRED:
      'This workspace requires two-factor authentication. Turn it on in your account settings.',
    AUTH_OAUTH_PROVIDER_DISABLED: 'This sign-in provider is not available.',
    AUTH_OAUTH_FAILED: 'Sign-in with the provider failed. Try again.',
    AUTH_OAUTH_EMAIL_UNVERIFIED: 'The provider did not confirm your email address.',
    PASSWORD_NOT_SET:
      'Your account signs in with Google or GitHub and has no password. Use “Forgot password?” to set one.',
    REAUTH_REQUIRED: 'For your security, sign out and sign in again, then try once more.',
    ACCOUNT_CONFIRM_MISMATCH: 'The email address does not match your account.',
    ACCOUNT_SOLE_OWNER:
      'You are the only owner of a workspace with other members. Make someone else an owner, or delete that workspace first.',
    ACCOUNT_BILLING_OWNER:
      'You are the billing owner of a workspace with other members. Hand billing to another owner in the workspace settings, or delete that workspace first.',
    STAFF_ACCOUNT: 'Smoke staff accounts are removed by a staff admin.',
    WORKSPACE_NOT_FOUND: 'Workspace not found, or you are not a member.',
    WORKSPACE_ROLE_INSUFFICIENT: 'Your role in this workspace does not allow this.',
    WORKSPACE_DELETION_PENDING: 'This workspace is scheduled for deletion.',
    MEMBER_NOT_FOUND: 'Member not found.',
    MEMBER_LAST_OWNER: 'A workspace needs at least one owner.',
    MEMBER_BILLING_OWNER:
      'This member is the billing owner. Hand billing to another owner in the workspace settings first.',
    BILLING_OWNER_NOT_OWNER:
      'Billing can only go to an owner of this workspace. Make them an owner first.',
    BILLING_OWNER_ONLY: 'Only the billing owner can hand billing over.',
    BILLING_OWNER_MISSING: 'This workspace has no billing owner. Please contact us.',
    INVITATION_INVALID: 'This invitation is invalid.',
    INVITATION_EXPIRED: 'This invitation has expired. Ask for a new one.',
    INVITATION_EMAIL_MISMATCH: 'This invitation was sent to a different email address.',
    INVITATION_ALREADY_MEMBER: 'You are already a member of this workspace.',
    CLIENT_NOT_FOUND: 'Client not found.',
    CLIENT_NOT_EMPTY: 'Move or delete the client’s projects first.',
    PROJECT_NOT_FOUND: 'Project not found.',
    PROJECT_ARCHIVED: 'This project is archived.',
    ASSET_NOT_FOUND: 'Asset not found.',
    ASSET_DATABASE_ENGINE_REQUIRED: 'A database asset needs one database engine and version.',
    ASSET_ADDRESSES_SERVER_ONLY: 'Only a server can have IP addresses.',
    ASSET_BASE_IMAGE_IMAGE_ONLY: 'Only a container image can have a base image.',
    DEPLOYMENT_INVALID: 'Only other assets can run on a server, and only in the same project.',
    INVENTORY_NOT_APPLICABLE: 'A database asset has no inventory to upload.',
    ADVISORY_NOT_FOUND: 'This workspace does not have that advisory.',
    ASSET_KIND_NOT_SUPPORTED: 'This asset type is not supported yet.',
    ASSET_ARCHIVED: 'This asset is archived.',
    API_TOKEN_NOT_FOUND: 'Token not found.',
    API_TOKEN_SCOPE_INVALID: 'These scopes are not valid for this token type.',
    API_TOKEN_NOT_RENAMABLE: 'Revoked tokens and MCP client grants cannot be renamed.',
    INVENTORY_NO_FILES: 'Add at least one file.',
    INVENTORY_FILE_TOO_LARGE: 'A file is larger than 20 MB.',
    INVENTORY_TOO_MANY_FILES: 'Too many files in one upload.',
    INVENTORY_UPLOAD_TOO_LARGE:
      'The upload is larger than 100 MB in total. Upload fewer files at a time.',
    INVENTORY_PARSE_FAILED: 'One or more files could not be parsed, so nothing was saved.',
    INVENTORY_LOCKFILE_REQUIRED:
      'package.json lists version ranges, not the versions you installed, so it cannot be checked precisely. Upload the lockfile as well: package-lock.json (npm install --package-lock-only), pnpm-lock.yaml (pnpm install --lockfile-only) or yarn.lock (yarn install).',
    SNAPSHOT_NOT_FOUND: 'Snapshot not found.',
    FINDING_NOT_FOUND: 'Finding not found.',
    FINDING_TRANSITION_NOT_ALLOWED: 'This status change is not allowed from the current status.',
    CURSOR_INVALID: 'The list changed. Reload it.',
    MITIGATION_NOT_FOUND: 'A selected mitigation is no longer offered. Reload the page.',
    FIX_PROMPT_TOO_MANY_FINDINGS: 'A combined prompt takes at most 10 findings.',
    FIX_PROMPT_MIXED_ASSETS: 'A combined prompt needs findings from one asset.',
    FIX_PROMPT_NOT_AVAILABLE: 'No fix prompt is available for this finding.',
    AI_REMEDIATION_UNAVAILABLE: 'An AI analysis cannot be generated for this finding right now.',
    EOL_NOTICE_NOT_FOUND: 'This end-of-life notice was not found.',
    EOL_PROMPT_NOT_AVAILABLE: 'This notice has no upgrade prompt.',
    INSUFFICIENT_SCOPE: 'This token does not have the scope for this action.',
    MITIGATION_NOT_AGENT_APPLICABLE: 'A coding agent cannot apply this mitigation.',
    NOTIFICATION_CHANNEL_NOT_CONFIGURED:
      'This chat channel is not set up on this Smoke server yet.',
    NOTIFICATION_CHANNEL_NOT_FOUND: 'This chat link no longer exists. Reload the page.',
    NOTIFICATION_ROUTE_INVALID:
      'A routing rule points to a client or group chat that no longer exists.',
    UNSUBSCRIBE_TOKEN_INVALID: 'This unsubscribe link is invalid or has expired.',
    WEBHOOK_SIGNATURE_INVALID: 'The request signature is not valid.',
    REMEDIATION_NOT_RATEABLE: 'This remediation cannot be rated.',
    DOWNLOAD_LINK_INVALID: 'This download link has expired. Open the list again for a fresh one.',
    PLAN_LIMIT_REACHED: 'The plan limit is reached. Upgrade the plan to add more.',
    PLAN_FEATURE_UNAVAILABLE: 'This is not part of the current plan. Upgrade to use it.',
    PLAN_NOT_FOUND: 'This plan is not available.',
    BILLING_NOT_CONFIGURED: 'Online payment is not set up on this Smoke server.',
    BILLING_DETAILS_REQUIRED: 'Add the billing details first.',
    BILLING_ALREADY_SUBSCRIBED: 'This workspace already has a paid plan. Use upgrade instead.',
    BILLING_NOT_SUBSCRIBED: 'Choose a paid plan first.',
    BILLING_NOT_AN_UPGRADE: 'This plan is not an upgrade of the current one.',
    BILLING_NOT_A_DOWNGRADE: 'This plan is not a downgrade of the current one.',
    BILLING_OPERATION_PENDING:
      'Another payment of this workspace is still being confirmed. Try again once it is settled.',
    BILLING_UNAVAILABLE:
      'The payment service is not available right now. Nothing was charged; try again later.',
    BILLING_PAYMENT_FAILED: 'The card was declined. Use another card and try again.',
    BILLING_WEBHOOK_DISABLED: 'Payment webhooks are off in mock mode.',
    BILLING_WEBHOOK_SIGNATURE_INVALID: 'The payment notification signature is not valid.',
    PAYMENT_NOT_FOUND: 'Payment not found.',
    REFUND_PENDING: 'A refund of this payment is still pending.',
    REFUND_NOT_ALLOWED:
      'This payment cannot be refunded here (card payments only, up to the amount paid).',
    STAFF_ONLY: 'This is for Smoke staff only.',
    STAFF_ROLE_INSUFFICIENT: 'This needs the staff admin role.',
    STAFF_ACCESS_REQUIRED: 'Open read-only access to this workspace with a reason first.',
    STAFF_READ_ONLY: 'Staff access is read-only. Nothing was changed.',
    USER_NOT_FOUND: 'No user with this email address. They need to sign up first.',
    FEATURE_FLAG_NOT_FOUND: 'Feature flag not found.',
    FALSE_POSITIVE_NOT_FOUND: 'This report no longer exists.',
    STAFF_ALERT_NOT_FOUND: 'This alert no longer exists.',
    REPORT_NOT_FOUND: 'Report not found.',
    REPORT_NOT_READY: 'The file is not ready yet.',
    REPORT_PERIOD_INVALID: 'The period must be 1 to 366 days, with valid dates.',
    GITHUB_NOT_CONFIGURED: 'GitHub is not set up on this Smoke server.',
    GITHUB_STATE_INVALID: 'The GitHub setup link expired. Start again.',
    GITHUB_INSTALLATION_NOT_FOUND: 'This GitHub installation is no longer connected.',
    GITHUB_REPOSITORY_NOT_FOUND: 'The repository is not available to the Smoke GitHub App.',
    GITHUB_REPOSITORY_ALREADY_TRACKED: 'This repository is already tracked in this workspace.',
    GITHUB_BRANCH_NOT_FOUND: 'The branch does not exist in the repository.',
    GITHUB_MANIFEST_PATH_INVALID: 'A manifest path is not valid.',
    GITHUB_API_ERROR: 'GitHub returned an error or could not be reached. Try again later.',
  },
  auth: {
    termsNotice:
      'By continuing, you agree to the <terms>terms of service</terms> and acknowledge the <privacy>privacy policy</privacy>.',
    loginTitle: 'Sign in to Smoke',
    loginSubtitle: 'Vulnerability tracking for agencies and small teams.',
    signUpTitle: 'Create your Smoke account',
    signUpSubtitle: 'See your first vulnerability report in minutes.',
    email: 'Email',
    password: 'Password',
    passwordHint: 'At least 12 characters. A passphrase works well.',
    displayName: 'Name',
    signIn: 'Sign in',
    signUp: 'Sign up',
    createAccount: 'Create account',
    noAccount: 'New to Smoke?',
    haveAccount: 'Already have an account?',
    forgotPassword: 'Forgot password?',
    or: 'or',
    continueWith: 'Continue with {{provider}}',
    oauthNotConfigured: 'Google and GitHub sign-in are not configured on this server.',
    checkEmailTitle: 'Check your email',
    checkEmailBody:
      'If {{email}} can be used for Smoke, we sent a link to verify it. The link is valid for 24 hours.',
    checkEmailGeneric:
      'Open the verification link we sent you. Need a new one? Enter your email address.',
    resendVerification: 'Send a new verification link',
    verificationResent: 'If the address needs verification, a new link is on its way.',
    backToSignIn: 'Back to sign in',
    verifyTitle: 'Verify your email',
    verifying: 'Verifying your email…',
    verified: 'Your email address is verified. You can sign in now.',
    twoFactorTitle: 'Two-factor authentication',
    twoFactorSubtitle: 'Enter the 6-digit code from your authenticator app.',
    recoverySubtitle: 'Enter one of your recovery codes. Each code works once.',
    authCode: 'Authentication code',
    recoveryCode: 'Recovery code',
    verify: 'Verify',
    useRecoveryCode: 'Use a recovery code',
    useAuthenticator: 'Use the authenticator app',
    cancel: 'Cancel',
    forgotTitle: 'Reset your password',
    forgotSubtitle: 'We will email you a link to set a new password.',
    sendResetLink: 'Send reset link',
    forgotSent: 'If an account uses this address, a reset link is on its way.',
    resetTitle: 'Set a new password',
    newPassword: 'New password',
    confirmPassword: 'Confirm the new password',
    setPassword: 'Set password',
    resetDone: 'Your password was changed. Sign in with the new password.',
  },
  account: {
    title: 'Account settings',
    profile: 'Profile',
    language: 'Language',
    languageHint: 'Used for the interface, emails, remediation and fix prompts.',
    loginMethods: 'Sign-in methods',
    twoFactor: 'Two-factor authentication',
    twoFactorBody: 'Protect your account with a code from an authenticator app (TOTP).',
    enabled: 'On',
    disabled: 'Off',
    setUp2fa: 'Set up two-factor authentication',
    scanQr: 'Scan this QR code with your authenticator app, or enter the key by hand.',
    qrAlt: 'QR code for the authenticator app',
    manualKey: 'Key',
    enterCode: 'Enter the 6-digit code the app shows.',
    enable2fa: 'Turn on',
    disable2fa: 'Turn off',
    regenerateCodes: 'New recovery codes',
    recoveryCodesTitle: 'Save your recovery codes now',
    recoveryCodesBody:
      'They are shown only once. Each code signs you in once if you lose your device.',
    downloadCodes: 'Download as text',
    password: 'Password',
    passwordBody: 'Changing your password signs out every other device.',
    currentPassword: 'Current password',
    changePassword: 'Change password',
    passwordChanged: 'Password changed. Other devices were signed out.',
    passwordMismatch: 'The new passwords do not match.',
    noPassword:
      'You sign in with Google or GitHub, so this account has no password. You can set one through “Forgot password?”.',
    setPasswordLink: 'Set a password',
    dangerZone: 'Danger zone',
    deleteAccount: 'Delete account',
    deleteAccountBody:
      'Deletes your account and signs you out everywhere. Your sign-in methods, two-factor settings, personal API tokens and MCP connections are removed. Activity in workspace audit logs is kept.',
    deleteTokensKept:
      'Only your account is deleted. Project and asset API tokens you created stay active in their workspaces, so CI that uses them keeps working. A workspace admin can revoke them on the API tokens page.',
    deleteAccountTitle: 'Delete your account?',
    deleteBlocking:
      'You are the only owner of these workspaces, which have other members. Make someone else an owner, or delete the workspace, before you delete your account:',
    deleteBillingOwner:
      'You are the billing owner of these workspaces, which have other members. Hand billing to another owner in the workspace settings, or delete the workspace, before you delete your account:',
    deleteWithAccount:
      'You are the only member of these workspaces. They are deleted with your account and purged within 30 days:',
    deleteLeaving: 'You leave these workspaces:',
    staffCannotDelete: 'Smoke staff accounts cannot be deleted here. Ask a staff admin.',
    recentSignIn:
      'Your account has no password, so you must have signed in within the last 15 minutes. If it has been longer, sign out and sign in again.',
    confirmEmail: 'Type your email address to confirm',
    deleteForever: 'Delete my account',
  },
  workspace: {
    welcomeTitle: 'Welcome to Smoke',
    createTitle: 'New workspace',
    createBody:
      'A workspace holds your clients, projects and findings. You can invite your team next.',
    name: 'Workspace name',
    nameHint: 'Usually your agency or team name.',
    timeZone: 'Time zone',
    timeZoneHint: 'Dates and SLA due dates are shown in this time zone.',
    create: 'Create workspace',
  },
  invitation: {
    title: 'Workspace invitation',
    accepting: 'Accepting the invitation…',
    signedInAs: 'You are signed in as {{email}}.',
  },
  members: {
    title: 'Members',
    description: 'Who can see and work on this workspace.',
    listTitle: 'Members',
    member: 'Member',
    role: 'Role',
    roleFor: 'Role of {{name}}',
    joined: 'Joined',
    you: 'You',
    billingOwner: 'Billing owner',
    remove: 'Remove',
    leave: 'Leave',
    confirmRemove:
      'Remove {{name}} from this workspace? Their personal API tokens stop working. Project and asset API tokens they created are kept and keep working.',
    confirmLeave:
      'Leave this workspace? Your personal API tokens stop working. Project and asset API tokens you created are kept and keep working.',
    inviteTitle: 'Invite a member',
    inviteBody: 'Invitations expire after 7 days.',
    sendInvite: 'Send invitation',
    invited: 'Invitation sent to {{email}}',
    pendingTitle: 'Pending invitations',
    noInvitations: 'No pending invitations',
    expires: 'Expires',
    expired: 'Expired',
    revoke: 'Revoke',
    rolesTitle: 'Roles',
    roleHelp: {
      owner: 'Everything, including billing and deleting the workspace.',
      admin: 'Members, channels and all projects.',
      member: 'The clients and projects assigned to them.',
    },
  },
  clients: {
    title: 'Clients & projects',
    description: 'Clients group projects; each project has one or more assets with an inventory.',
    new: 'New client',
    newBody: 'For example the company you maintain a site for.',
    name: 'Client name',
    renameTitle: 'Rename client',
    showArchived: 'Show archived',
    noClient: 'Without a client',
    projectCount_one: '{{count}} project',
    projectCount_other: '{{count}} projects',
    emptyTitle: 'No clients yet',
    emptyBody: 'Create a client, add a project and upload a lockfile to see its vulnerabilities.',
  },
  projects: {
    new: 'New project',
    name: 'Project name',
    client: 'Client',
    environment: 'Environment',
    description: 'Description',
    descriptionHint: 'Optional. What this project is, so the team can tell it apart.',
    environmentHint: 'Staging and development projects get one priority level lower.',
    editTitle: 'Edit project',
    openFindings: 'Open findings',
    noOpen: 'None open',
    viewOpenFindings_one: 'View {{count}} open finding',
    viewOpenFindings_other: 'View {{count}} open findings',
    viewFindings: 'View findings',
    emptyTitle: 'No projects yet',
    emptyBody: 'Add a project for this client.',
    archivedNotice: 'This project is archived: it is not scanned and sends no notifications.',
  },
  stack: {
    title: 'Stack',
    hint: 'Used only to detect end-of-life software and as context for fix prompts; never for vulnerability matching.',
    assetBody: 'Detected from this asset’s inventory, plus anything added by hand.',
    onThisServer: 'From the assets on this server',
    fromServers: 'From the servers it runs on',
    archivedBody: 'Archived assets are left out of the stack.',
    manualTitle: 'Add to the stack by hand',
    manualBody:
      'Only for what cannot be detected, such as Node.js installed with nvm or a PostgreSQL built from source.',
    generatedBody:
      'Generated from the assets of this project: host inventories give the operating system and installed services, lockfiles the frameworks. Add what cannot be detected on the asset page.',
    emptyTitle: 'No stack yet',
    emptyBody: 'It appears once a host inventory or a lockfile is uploaded.',
    removeDeclared: 'Remove',
    database: 'Database',
    service: 'Service',
    addDatabase: 'Add database',
    addService: 'Add service',
    runtime: 'Runtime',
    framework: 'Framework',
    os: 'Operating system',
    version: 'version',
    release: 'release',
    invalidVersion: 'Use numbers with dots only, for example 20, 8.3 or 22.04.',
    choose: 'Choose…',
    addRuntime: 'Add runtime',
    addFramework: 'Add framework',
    addOs: 'Add operating system',
    itemName: '{{kind}} {{n}}',
    itemVersion: '{{kind}} {{n}} {{version}}',
    removeItem: 'Remove {{kind}} {{n}}',
    limit: 'Up to {{count}}.',
    duplicate: 'The same item and version are listed twice.',
    notTracked: 'No end-of-life data, so no end-of-life notices.',
    notTrackedLatest:
      'No end-of-life data, so no end-of-life notices. The newest version is {{version}}.',
    group: {
      js: 'JavaScript / TypeScript',
      php: 'PHP',
      python: 'Python',
      other: 'Other languages',
    },
  },
  catalogDelete: {
    confirm: 'Delete',
    typeName: 'Type “{{name}}” to confirm',
    reason: 'Reason (optional)',
    archiveInstead:
      'Deleted items and their data are purged after 30 days and cannot be restored in Smoke. To stop scanning and notifications but keep the history, archive instead.',
    client: 'Delete client',
    clientTitle: 'Delete the client “{{name}}”?',
    clientBody:
      'Only a client without projects can be deleted: move its projects to another client or delete them first. Member assignments to this client are removed.',
    project: 'Delete project',
    projectTitle: 'Delete the project “{{name}}”?',
    projectBody:
      'The project and all its assets are removed from Smoke, with their inventories and findings. API tokens for this project and its assets stop working.',
    asset: 'Delete asset',
    assetTitle: 'Delete the asset “{{name}}”?',
    assetBody:
      'The asset is removed from Smoke, with its inventories and findings. API tokens for this asset stop working.',
    deleted: 'Deleted.',
  },
  servers: {
    runsOn: 'Runs on:',
    new: 'New server',
    newBody:
      'Create the server first, then add its repositories, Docker images and databases. It can be created before it is scanned.',
    addExisting: 'Add existing asset',
    addNew: 'Add new here',
    unlink: 'Remove',
    place: 'Place on a server',
    nothingOnServer: 'Nothing else is on this server yet.',
    unplacedTitle: 'Not on a server',
    unplacedBody: 'For example a repository not deployed yet, or a managed service such as RDS.',
    addTitle: 'Run an asset on a server',
    editTitle: 'Edit ports and domains',
    reachNote:
      'Ports and domains are optional. Only members of this project can see them; they are never in notifications or fix prompts and are not sent to AI.',
    server: 'Server',
    asset: 'Asset',
    ports: 'Ports',
    portsHint: 'Separated by commas, for example 443, 8080.',
    portsInvalid: 'A port is a number from 1 to 65535.',
    domains: 'Domains',
    domainsHint: 'Separated by commas, for example api.example.com.',
    addresses: 'IP addresses',
    addressesHint: 'Separated by commas, IPv4 or IPv6.',
    addressesNote:
      'Optional. Only members of this project can see them; they are never in notifications or fix prompts and are not sent to AI.',
    noAddresses: 'None yet.',
    engine: 'Database engine',
    engineVersion: 'Version',
    databaseTitle: 'Database',
    databaseNote:
      'Tracks the end-of-life date; vulnerabilities are not matched yet. A self-hosted database installed with apt or apk is detected from the host inventory.',
  },
  assets: {
    baseImage: 'Base image',
    baseImageHint:
      'Optional: the Dockerfile FROM, e.g. node:20-alpine, for more precise base-image advice from the AI. Without it, the inventory’s distribution is used (e.g. alpine:3.24). Only public images are sent: Docker Hub official images and well-known publishers such as prom, nginxinc, bitnami and grafana; your own registry or organization is not sent and the distribution is used instead.',
    aiBaseImage: 'Base image for the AI analysis',
    aiBaseImageTyped: 'as typed',
    aiBaseImageDerived: 'from the inventory’s distribution',
    aiBaseImageNotSent:
      '{{typed}} is not a public image, so it is not sent; the distribution is used instead.',
    baseImageInvalid: 'Enter an image name, e.g. node:20-alpine.',
    noInventoryOs:
      'No inventory yet. Click Collect with a command, run it, and upload the file it saves.',
    kind: {
      manifest_upload: 'Lockfiles or manifests',
      manifest_uploadHint: 'An application: upload its lockfiles or an SBOM.',
      host: 'Linux server',
      hostHint:
        'A server or VM running Ubuntu, Debian or Alpine: run a command and upload the file it saves.',
      container_image: 'Container image',
      container_imageHint:
        'An image based on Ubuntu, Debian or Alpine: run the command in it, or upload its SBOM.',
      database: 'Database',
      databaseHint:
        'Self-hosted or managed: enter its engine and version to track its end-of-life date. Vulnerabilities are not matched yet.',
    },
    repositoryUrl: 'Repository URL',
    repositoryUrlHint:
      'Optional. Lets coding agents find this asset from `git remote get-url origin`.',
    title: 'Assets',
    description: 'Each asset has its own inventory: a set of lockfiles or an SBOM.',
    new: 'New asset',
    newBody: 'For example one application or service in the project.',
    name: 'Asset name',
    nameHint: 'For example “web” or “api”.',
    kindNote: 'Servers, container images and databases count toward the plan like other assets.',
    type: 'Type',
    lastInventory: 'Last inventory',
    never: 'Never',
    emptyTitle: 'No assets yet',
    emptyBody: 'Start with a server, then add its repositories, Docker images and databases.',
    lastInventoryAt: 'Last inventory {{date}}',
    noInventory: 'No inventory yet. Upload lockfiles below.',
    editTitle: 'Edit asset',
    archivedBody:
      'An archived asset is not scanned and sends no notifications. Unarchive it to upload again.',
    archivedNotice: 'This asset is archived.',
    privateLookup: {
      askTitle_one: '{{count}} package comes from a non-public registry',
      askTitle_other: '{{count}} packages come from a non-public registry',
      body: 'They may be your own internal packages, or come through your company registry proxy. To suggest a parent package upgrade, Smoke looks up the packages on a dependency path on the public registries (npm, PyPI, Packagist), which sends their names there. Their versions and your code are never sent.',
      untilDecided:
        'Until you decide, these names are not sent, so fixes through them may be incomplete. AI remediation never receives them either way.',
      allow: 'Allow lookups',
      deny: 'Do not send',
      adminOnly: 'An Admin of this workspace decides this.',
      allowed_one: '{{count}} private package: its name may be looked up on the public registries.',
      allowed_other:
        '{{count}} private packages: their names may be looked up on the public registries.',
      denied_one: '{{count}} private package: its name is not sent to the public registries.',
      denied_other:
        '{{count}} private packages: their names are not sent to the public registries.',
    },
  },
  upload: {
    osDescription:
      'Click Collect with a command, run it, then upload the file it saves or paste its content (images can also upload a CycloneDX SBOM). Smoke keeps only package names and versions and the OS facts listed in the collect dialog.',
    osDropTitle: 'Drop the file the command saved here',
    osDropHint: 'Up to 20 MB per file.',
    hostFormats: 'smoke-host.txt',
    imageFormats: 'smoke-image.txt · CycloneDX / SPDX JSON',
    osPath: 'File',
    title: 'Upload inventory',
    description:
      'Lockfiles or SBOMs for this asset. Smoke stores only package names, versions and paths; files are deleted within 24 hours.',
    dropTitle: 'Drop lockfiles or a folder here',
    dropHint: 'Several manifests from a monorepo keep their relative paths. Up to 20 MB per file.',
    chooseFiles: 'Choose files',
    chooseFolder: 'Choose folder',
    formats:
      'package-lock.json · npm-shrinkwrap.json · pnpm-lock.yaml · yarn.lock · poetry.lock · uv.lock · Pipfile.lock · requirements.txt · composer.lock · CycloneDX / SPDX JSON',
    ignoredFromFolder_one:
      '{{count}} file in the folder is not a supported format and was left out.',
    ignoredFromFolder_other:
      '{{count}} files in the folder are not in a supported format and were left out.',
    selected: 'Selected files',
    path: 'Path in the repository',
    pathFor: 'Path of {{name}}',
    size: 'Size',
    removeFile: 'Remove {{path}}',
    tooLarge: 'Larger than 20 MB. ',
    unsupported: 'Not a supported manifest name. ',
    tooMany: 'At most {{max}} files per upload.',
    duplicatePaths: 'Two files have the same path. Edit the paths so each is unique.',
    rejected: 'Upload rejected',
    wholeInventory:
      'An upload replaces the asset’s whole inventory. If any file fails, nothing is saved.',
    clear: 'Clear',
    submit_one: 'Upload {{count}} file',
    submit_other: 'Upload {{count}} files',
  },
  report: {
    osLeftOut: {
      notInstalled_one: '{{count}} package was listed but not installed and was left out.',
      notInstalled_other: '{{count}} packages were listed but not installed and were left out.',
      malformed_one: '{{count}} line could not be read and was left out.',
      malformed_other: '{{count}} lines could not be read and were left out.',
      unknownSections_one: '{{count}} unknown section was ignored.',
      unknownSections_other: '{{count}} unknown sections were ignored.',
    },
    title: 'Parse report',
    files: 'Files',
    components: 'Components',
    skipped: 'Skipped',
    unresolved: 'Unresolved',
    matching: 'Matching',
    viewFindings: 'View findings',
    snapshotLine: 'Snapshot #{{sequence}}, uploaded {{date}}',
    matchFailed: 'Matching failed. Smoke retries automatically.',
    customSourceTitle: 'Packages from a non-default registry',
    customSourceBody:
      'These packages come from a registry other than the public default. Smoke matches them as usual, but each may be a private package with the same name as a public one, so a finding can be a false positive.',
    componentCount_one: '{{count}} component',
    componentCount_other: '{{count}} components',
    skippedCount_one: '{{count}} skipped',
    skippedCount_other: '{{count}} skipped',
    unresolvedCount_one: '{{count}} unresolved',
    unresolvedCount_other: '{{count}} unresolved',
    entry: 'Entry',
    line: 'Line',
    reason: 'Reason',
    clean: 'Everything in this file was resolved.',
  },
  snapshots: {
    title: 'Snapshot history',
    description: 'The last 30 inventories of this asset. Pick two to compare.',
    uploaded: 'Uploaded',
    source: 'Source',
    current: 'Current',
    compareFrom: 'From',
    compareTo: 'To',
    compareFromN: 'Compare from snapshot {{n}}',
    compareToN: 'Compare to snapshot {{n}}',
    viewReport: 'View report',
    compareHint: 'Pick a “from” and a “to” snapshot to see what changed.',
    emptyTitle: 'No snapshots yet',
    emptyBody: 'Upload the asset’s lockfiles to create the first snapshot.',
    osEmptyBody: 'Upload the file the command saved to create the first snapshot.',
  },
  diff: {
    title: 'Changes between snapshots',
    summary:
      '#{{from}} → #{{to}}: {{added}} added, {{removed}} removed, {{upgraded}} changed, {{unchanged}} unchanged',
    change: 'Change',
    versions: 'Version',
    added: 'Added',
    removed: 'Removed',
    upgraded: 'Changed',
  },
  dashboard: {
    description: 'Open findings by priority and SLA. Days are counted in {{timeZone}}.',
    scope: 'Show the dashboard for',
    allClients: 'All clients',
    allProjects: 'All projects',
    open: 'open',
    openByPriority: 'Open findings by priority',
    noOpenTitle: 'No open findings',
    noOpenBody: 'Nothing needs fixing right now.',
    topProjects: 'Projects with the most urgent findings',
    trendTitle: 'Open findings, last 90 days',
    trendDescription: 'Open findings at the end of each day, one panel per priority.',
    trendScale: 'All panels share one scale, from 0 to {{max}}.',
    trendSummary: '{{priority}}: {{first}} open {{days}} days ago, {{last}} open now.',
    trendDelta: '{{delta}} in 90 days',
    trendFlat: 'unchanged in 90 days',
    trendTable: 'Show the numbers as a table',
    date: 'Date',
    noTrendTitle: 'No history yet',
    noTrendBody: 'The trend starts with the first uploaded inventory.',
    sla: 'SLA status',
    slaLink_one: '{{count}} finding {{state}}',
    slaLink_other: '{{count}} findings {{state}}',
    allFixes: 'All recently fixed',
    slaHint: 'Due soon means within 2 days of the due date.',
    recentFixes: 'Recent fixes',
    noFixesTitle: 'No fixes yet',
    noFixesBody:
      'Smoke marks a finding fixed when a newer inventory no longer has the vulnerable version.',
    onboardTitle: 'Your first report is three steps away',
    onboardBody:
      'Smoke matches your lockfiles against public advisories and tells you what to fix first.',
    onboardAction: 'Add a client',
    step1: 'Create a client, for example “ACME Corp”.',
    step2: 'Add a project and an asset.',
    step3: 'Upload package-lock.json, composer.lock or another lockfile.',
  },
  eol: {
    title: 'End-of-life notices',
    description:
      'Runtimes, frameworks and operating systems past or near end of life. Listed separately from P1–P4.',
    projectDescription: 'From the stack of this project.',
    emptyTitle: 'No end-of-life notices',
    emptyBody:
      'The stack is generated from the assets. You get a notice when something in it reaches end of life.',
    emptyProjectTitle: 'No end-of-life notices',
    emptyProjectBody: 'Nothing in this project’s stack has reached or is close to end of life.',
    headline: {
      eol: '{{product}} is end-of-life (since {{date}})',
      soon: '{{product}} reaches end of life on {{date}}',
      other: '{{product}}',
    },
    latest: 'Latest release {{version}}',
    releaseNotes: 'Release notes',
    advice: {
      runtime:
        'We suggest upgrading to {{target}}. Check that your dependencies support it, then update the version settings and CI.',
      framework:
        'We suggest upgrading to {{target}}. Across several major versions, go one at a time and run the tests each time.',
      os: 'We suggest planning an upgrade or a move to {{target}}: check that the software on it supports the new version, try it in a test environment, and back up first.',
      database:
        'We suggest upgrading to {{target}}: back up, check the official upgrade path and try it in a test environment before the production database.',
      service:
        'We suggest upgrading to {{target}}: check that your configuration still works and try it in a test environment first.',
    },
    adviceUntil: 'Supported until {{date}}.',
    adviceNone:
      'No newer supported release was found. Check the official announcement and consider alternatives.',
    promptGenerate: 'Generate upgrade prompt',
    promptRegenerate: 'Generate prompt again',
    promptView: 'View prompt',
    promptViewOld: 'View old version',
    promptCopy: 'Copy prompt',
    promptTitle: 'Upgrade {{product}} to {{target}}',
    promptCaption:
      'Generated {{date}} by {{name}}. Paste it into Claude Code, Cursor, GitHub Copilot or Codex.',
    promptOutdatedTitle: 'This prompt is out of date',
    promptOutdatedBody:
      'The end-of-life information or the suggested version changed after it was generated. We suggest generating it again. The old version stays.',
    promptGoneBody: 'This item is no longer in the stack. The prompt is kept for reference.',
    promptGoneTitle: 'Prompts for items no longer in the stack',
    promptOthers: 'Other versions',
  },
  findings: {
    title: 'Findings',
    description: 'Most urgent first. Filters are kept in the address, so you can share a view.',
    moreAdvisories_one: '+{{formatted}} more',
    moreAdvisories_other: '+{{formatted}} more',
    count_one: '{{count}}{{more}} finding',
    count_other: '{{count}}{{more}} findings',
    total_one: '{{count}} finding · {{shown}} shown',
    total_other: '{{count}} findings · {{shown}} shown',
    views: 'Views',
    viewAll: 'All findings',
    viewFixed: 'Recently fixed',
    clearFilters: 'Clear {{count}} filters',
    selectAll: 'Select all shown findings',
    select: 'Select {{name}}',
    selected_one: '{{count}} selected',
    selected_other: '{{count}} selected',
    bulkLabel: 'Actions for the selected findings',
    bulkDone: '{{updated}} updated, {{skipped}} skipped',
    assign: 'Assign',
    snooze: 'Snooze',
    snoozeBody: 'The finding returns to open on that date.',
    snoozeUntil: 'Snooze until',
    ignore: 'Ignore',
    ignoreBody:
      'Ignored findings stay visible under the status filter. A new advisory for the same version creates a new finding.',
    ignoreReason: 'Reason',
    ignoreExpiry: 'Reopen on',
    ignoreExpiryHint: 'Optional. The finding reopens on this date.',
    note: 'Note',
    combinedRule: 'Combined prompt: up to {{max}} findings from one asset.',
    clearSelection: 'Clear selection',
    emptyTitle: 'No findings',
    emptyBody: 'Upload an inventory to see its vulnerabilities here.',
    emptyFilteredTitle: 'No findings match these filters',
    emptyFilteredBody: 'Change or clear the filters.',
  },
  filters: {
    label: 'Filter findings',
    any: 'Any',
    package: 'Package',
    advisory: 'Advisory ID',
    search: 'Search',
    client: 'Client',
    project: 'Project',
    asset: 'Asset',
    selectedAsset: 'Selected asset',
    unresolved: 'Unresolved (default)',
    allStatuses: 'All statuses',
    ecosystem: 'Ecosystem',
    scope: 'Scope',
    kevYes: 'Known exploited',
    kevNo: 'Not in KEV',
    assignedToMe: 'Assigned to me',
    unassigned: 'Unassigned',
  },
  finding: {
    kernelState: {
      old: 'Old kernel, not running',
      oldTitle: 'Installed but not running, so always P4. Remove it.',
      newer: 'New kernel, not running yet',
      newerTitle: 'Installed but not running until the next reboot',
    },
    distroWontFix: 'Distro won’t fix',
    distroWontFixTitle:
      'The distribution marked every advisory as one it will not fix. Sent in digests only.',
    priority: 'Priority',
    package: 'Package',
    where: 'Project / asset',
    risk: 'Risk',
    fixState: 'Fix',
    status: 'Status',
    sla: 'SLA',
    fixedAt: 'Fixed',
    assignee: 'Assignee',
    transitive: 'transitive',
    direct: 'direct',
    kevTitle: 'Listed in CISA Known Exploited Vulnerabilities',
    advisoryCount_one: '{{count}} advisory',
    advisoryCount_other: '{{count}} advisories',
    details: 'Details',
    ecosystem: 'Ecosystem',
    scope: 'Scope',
    relationship: 'Dependency',
    severity: 'Severity',
    slaFix: 'Fix SLA',
    slaMitigate: 'Mitigate or accept risk',
    firstSeen: 'First seen',
    lastSeen: 'Last seen',
    priorityReason: 'Why this priority',
    finalPriority: 'Result: {{priority}}',
    paths: 'Where it is installed',
    manifest: 'Manifest',
    dependencyPaths: 'Dependency paths',
    actions: 'Actions',
    transition: {
      open: 'Reopen',
      in_progress: 'Start work',
      pending_verification: 'Mark as fixed (pending verification)',
    },
    markMitigated: 'Record an applied mitigation',
    reportFalsePositive: 'Report false positive',
    falsePositiveBody: 'Smoke staff review the report. The finding stays as it is until then.',
    falsePositiveScope_one: 'The report covers the finding’s {{count}} active advisory.',
    falsePositiveScope_other: 'The report covers the finding’s {{count}} active advisories.',
    falsePositiveQueue: 'Goes to the review queue',
    falsePositiveSent: 'Thanks. The report is in the review queue.',
    sendReport: 'Send report',
    fixedBySmoke:
      'Only Smoke marks a finding fixed, from a newer inventory without the vulnerable version.',
    statusChanged: 'Status changed to {{status}}',
    customSourceTitle: 'Possibly a private package',
    customSourceBody:
      'This package was installed from a registry other than the public default. It may be a private package with the same name, in which case this finding does not apply.',
    inventoryGaps:
      'The inventory has unresolved entries, so Smoke may not see every package. See the asset’s parse report.',
    ignoredTitle: 'Ignored: {{reason}}',
    reopensOn: 'Reopens on {{date}}.',
    snoozedUntil: 'Snoozed until {{date}}.',
    mitigatedTitle: 'Mitigated, still installed',
    mitigatedBody:
      'A mitigation was applied, but the vulnerable version is still installed. It counts as open until fixed.',
    fixedBody: 'Fixed (verified) {{date}}: a newer inventory no longer has the vulnerable version.',
    pendingBody:
      'Waiting for verification: upload the updated lockfile, or submit it through MCP, and Smoke confirms the fix.',
  },
  manualPriority: {
    badge: 'Manual P1',
    raise: 'Raise to P1',
    raiseTitle: 'Raise {{id}} to P1',
    raiseBody:
      'Every affected asset in this workspace becomes P1, and assets added later too. The SLA starts now and an escalation is sent. A kernel that is not running stays P4.',
    reason: 'Reason',
    reasonHint:
      'Why this CVE comes first. Members who can see the finding see the reason, and it goes in the audit log.',
    raised: '{{id}} raised to P1.',
    by: 'Raised to P1 by {{name}} on {{date}}',
    unknown: 'a removed user',
    kernelStaysP4: 'This kernel is not running, so this finding stays P4.',
    cancel: 'Cancel manual P1',
    cancelTitle: 'Cancel the manual P1 of {{id}}?',
    cancelBody: 'Priorities and SLA due dates go back to what the rules compute.',
    cancelled: 'Manual P1 of {{id}} cancelled.',
    title: 'CVEs raised to P1',
    description:
      'Advisories an Admin judged to come first, for the whole workspace. Raise one from the advisory list of a finding.',
    emptyTitle: 'No CVEs raised to P1',
    emptyBody: 'Priorities all come from the rules for now.',
    advisory: 'Advisory',
    raisedBy: 'Raised by',
    openFindings: 'Open findings',
  },
  advisory: {
    title_one: '{{count}} advisory',
    title_other: '{{count}} advisories',
    aliases: 'Also known as',
    percentile: 'percentile {{value}}',
    references: '{{count}} references (third-party links)',
    withdrawn: 'Withdrawn',
    thirdParty: 'Advisory text (third-party content, not reviewed by Smoke)',
    thirdPartyLabel: 'Advisory text from a third party',
    showDetails: 'Show details',
    truncated: 'Shortened. The full text is in the advisory linked below.',
    noFixYet: 'No fix yet',
    source: 'Source:',
    sourceSeparator: ', ',
    processedBySmoke: ', processed by Smoke.',
    epssByFirst: 'EPSS scores by FIRST.',
    urgentTitle: 'Advisories to deal with first',
    urgentDescription:
      'Sorted automatically by manual P1, KEV, severity, CVSS score and EPSS: the first {{shown}} of {{total}}.',
    viewAll: 'View all',
    searchLabel: 'Search by advisory ID',
    sortLabel: 'Sort by',
    sortUrgency: 'Overall urgency',
    sortCvss: 'CVSS score',
    sortEpss: 'EPSS',
    filterPriority: 'Only {{priority}} ({{count}})',
    showMore_one: 'Show more ({{formatted}} left)',
    showMore_other: 'Show more ({{formatted}} left)',
    noMatch: 'No matching advisories.',
    loadText: 'Load advisory text',
    textUnavailable: 'The advisory text is not available right now.',
  },
  fix: {
    distroCandidates: 'Fixed versions (from the distribution’s security data)',
    title: 'Fix availability',
    stale: 'Last known state; Smoke could not refresh it',
    staleBody:
      'smoke-signal could not be reached. This is the state from {{date}}; Smoke retries automatically.',
    coverage: 'An upgrade fixes {{covered}} of {{total}} advisories.',
    packageManager: 'Package manager: {{name}}',
    overrideSupported: 'override supported ({{mechanism}})',
    overrideUnsupported: 'no override support',
    checkedAt: 'checked {{date}}',
    candidates: 'Fixed versions (verified in the registry)',
    version: 'Version',
    delta: 'Change',
    published: 'Published',
    fixes: 'Fixes',
    fixesCount: '{{count}} of {{total}}',
    deprecated: 'deprecated',
    prerelease: 'pre-release',
    noCandidates:
      'No released version fixes these advisories yet. Smoke checks daily and tells you when a fix appears.',
    parents: 'Parent packages',
    parentRefresh: 'current range already allows a fixed version: refresh the lockfile',
    parentUpgrade: 'upgrade to {{versions}}',
    parentNone: 'no parent version allows a fix yet',
    uncheckedParents_one: '{{count}} more parent was not checked.',
    uncheckedParents_other: '{{count}} more parents were not checked.',
  },
  remediation: {
    aiLinuxTitle: 'AI analysis for Linux packages is not switched on',
    aiLinuxBody: 'The steps above are Smoke’s own and complete without it.',
    title: 'Remediation',
    description:
      'Steps are rendered by Smoke for the package manager. Names, versions and commands never come from AI.',
    target: 'Target: {{pkg}} {{version}}',
    file: 'File: {{file}}',
    alternatives: 'Alternatives ({{count}})',
    noDeterministic: 'No upgrade path is available. See the mitigations.',
    aiTitle: 'AI analysis',
    aiUnavailable: 'AI analysis unavailable',
    aiUnavailableBody: 'The steps above are complete without it. Smoke retries the analysis later.',
    aiQueued: 'Being prepared. This page updates when it is ready.',
    aiNotGenerated:
      'The AI analysis adds breaking-change risk, exploitability and code change hints, and goes into the fix prompt. It is only generated when you ask, and it uses your AI allowance.',
    aiGenerate: 'Generate AI analysis',
    aiRegenerate: 'Generate again',
    aiGeneratedAt: 'Generated {{date}}',
    aiOutdatedTitle: 'This content is out of date',
    aiOutdatedBody: 'We suggest generating it again. The old content stays in the version history.',
    aiRegenerateFailed: 'The last attempt to generate it again failed. Try again later.',
    versionsTitle: 'Version history',
    versionsEmpty: 'Nothing generated yet.',
    versionCurrent: 'Current',
    versionOutdated: 'Out of date',
    versionAutomatic: 'Generated automatically',
    versionView: 'View',
    versionTitle: 'Version of {{date}}',
    versionNoPrompt: 'This version has no saved fix prompt.',
    breakingRisk: 'Breaking-change risk',
    exploitability: 'Exploitability',
    restartImpact: 'Restart impact',
    containerRedeploy: 'Containers need no service restart: redeploy them from the new image.',
    aiBaseImageEdit: 'Enter or change the base image on the asset page',
    aiPublicServices:
      'The AI only received service names that distribution packages provide; custom services are not sent, so rely on Smoke’s steps above.',
    baseImageAdvice: 'Base image advice',
    maintenanceNotes: 'Maintenance notes',
    codeHints: 'Code-change hints',
    unsourcedHint:
      'Not backed by release notes or the advisory: confirm in the package documentation',
    verification: 'What to verify',
  },
  rating: {
    question: 'Was this remediation helpful?',
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
    reason: 'What was wrong?',
    submit: 'Send',
    thanks: 'Thanks for the feedback.',
  },
  mitigation: {
    title: 'Interim mitigations',
    intro: {
      none: 'No fix available',
      blocked: 'A fix exists but this project cannot reach it yet',
      partial: 'The upgrade fixes only some advisories',
    },
    introBody:
      'These steps reduce the risk until a fix can be installed. Smoke tells you as soon as a fix is available.',
    partialCoverage:
      'The upgrade fixes {{covered}} of {{total}} advisories. These mitigations cover the rest.',
    noSourced: 'No sourced mitigations are available for these advisories yet.',
    group: {
      sourced: 'From public sources',
      ai_suggestion: 'AI suggestions',
      generic: 'Generic mitigations',
    },
    label: {
      generic: 'Generic',
      aiSuggestion: 'AI suggestion, no source quotes',
      aiSuggestionQuoted: 'AI suggestion, quoted from the advisory',
      aiSummaryUnreviewed: 'AI summary of public sources, not yet reviewed',
      englishOnly: 'Available in English only',
      reviewed: 'Reviewed',
    },
    impact: 'Impact: {{level}}',
    official: 'official',
    preconditions: 'Preconditions',
    quotedFrom: 'From {{advisory}} ({{field}})',
    advisoryField: { summary: 'summary', details: 'details' },
    inferred: 'Inferred from the advisory text; the advisory does not say it directly.',
    conflicts: 'Sources disagree',
    peopleOnly: 'For people only: never included in a coding-agent prompt.',
    other: 'Other mitigation',
    otherNote: 'What did you do?',
    reviewAt: 'Review on',
    reviewAtHint: 'Smoke reminds you on this date (default 30 days).',
    apply_one: 'Mark {{count}} mitigation applied',
    apply_other: 'Mark {{count}} mitigations applied',
    applied: 'Mitigation recorded. The finding is now mitigated.',
    appliedTitle: 'Applied mitigations',
    reviewOn: 'Review on {{date}}.',
  },
  prompt: {
    copy: 'Copy fix prompt',
    copyMitigation: 'Copy mitigation task',
    preview: 'Preview prompt',
    title: 'Fix prompt',
    mitigationTitle: 'Mitigation task',
    combined: 'Copy combined prompt',
    combinedTitle: 'Combined fix prompt',
    templateVersion:
      'Template {{version}}. Paste it into Claude Code, Cursor, GitHub Copilot or Codex.',
    skipped_one: '{{count}} finding has no prompt and was left out.',
    skipped_other: '{{count}} findings have no prompt and were left out.',
  },
  history: {
    title: 'History',
    smoke: 'Smoke',
    unassigned: 'Unassigned',
    snapshot: {
      title: 'How it stood when completed',
      assignee: 'Assignee',
      priority: 'Priority',
      package: 'Package',
      asset: 'Asset',
      advisories: 'Vulnerabilities',
      more: 'and {{count}} more',
      sla: 'SLA',
      slaMet: 'Met (due {{date}})',
      slaMissed: 'Missed (due {{date}})',
      mitigations: 'Mitigations',
    },
  },
  people: {
    left: '{{name}} (left)',
    deleted: 'Deleted account',
  },
  settings: {
    title: 'Workspace settings',
    description: 'Name, time zone, security and SLA.',
    general: 'General',
    adminOnly: 'Only workspace admins can change these settings.',
    enforce2fa: 'Require two-factor authentication',
    enforce2faHint: 'Owners only. Members without it must turn it on before they continue.',
    dangerZone: 'Danger zone',
    deleteWorkspace: 'Delete workspace',
    deleteWorkspaceBody:
      'Owners only. Everyone loses access at once, every API token is revoked, and all data is purged within 30 days.',
    deleteWorkspaceTitle: 'Delete “{{name}}”?',
    billingOwner: {
      title: 'Billing owner',
      description:
        'This workspace counts toward the plan and usage of its billing owner’s account. The billing owner is an owner; when billing goes to another owner, their plan must hold this workspace’s projects and assets.',
      you: '{{name}} (you)',
      none: 'No billing owner: the Free plan limits apply.',
      newOwner: 'Hand to',
      transfer: 'Hand over billing',
      noCandidates:
        'No other owner can take over. Make a member an owner on the Members page first.',
      confirmTitle: 'Hand billing to {{name}}?',
      confirmBody:
        'The workspace then uses {{name}}’s plan and counts toward their usage; your plan no longer covers it.',
      done: 'Billing handed to {{name}}.',
    },
    deleteWorkspaceBilling:
      'The workspace stops counting toward its billing owner’s plan. The plan itself is not canceled (cancel it on the billing page), and nothing is refunded automatically.',
    typeName: 'Type the workspace name to confirm',
    reason: 'Reason (optional)',
    deleteWorkspaceConfirm: 'Delete workspace',
    workspaceDeleted: 'The workspace is scheduled for deletion.',
  },
  sla: {
    title: 'SLA policy',
    description: 'Days to fix (or mitigate, when there is no fix) per priority.',
    days: 'days',
    range: 'Between 1 and 365',
    recompute: 'Due dates of open findings are recomputed when you save.',
    saved: 'SLA policy saved. Due dates were recomputed.',
  },
  tokens: {
    oauthClient: 'Client',
    lastClient: 'last used by {{name}}',
    projects: 'Projects',
    allProjects: 'All projects',
    restrictProjects: 'Restrict to projects',
    restrictProjectsHint:
      'Optional. Without a selection, the token reaches every project you can access.',
    oauthNote:
      'Created when a coding agent signed in with OAuth. Revoke it to disconnect the agent.',
    title: 'API tokens',
    description:
      'For the CLI, CI and the MCP server. Tokens are stored as hashes and expire after at most a year.',
    new: 'New token',
    name: 'Name',
    kind: 'Type',
    kindHint: 'A project token can only reach that project.',
    scopes: 'Scopes',
    prefix: 'Token',
    lastUsed: 'Last used',
    expires: 'Expires',
    expiresIn: 'Expires in (days)',
    expiresHint: '1 to 365 days.',
    never: 'Never',
    revoked: 'Revoked',
    revoke: 'Revoke',
    confirmRevoke: 'Revoke “{{name}}”? Anything using it stops working.',
    rename: 'Rename',
    renameTitle: 'Rename token',
    renameHint:
      'Only the name changes: the token, its scopes and expiry stay, so nothing using it is affected.',
    shownOnce: 'Copy the token now. It is shown only once.',
    done: 'I have copied it',
    chooseProject: 'Choose a project',
    emptyTitle: 'No API tokens',
    emptyBody: 'Create a token to use Smoke from CI or a coding agent.',
  },
  mcp: {
    open: 'Coding agent setup',
    chooseProject: 'Choose the project the repository belongs to',
    title: 'Coding agent setup (MCP)',
    description:
      'Connect Claude Code, Cursor, GitHub Copilot or Codex to Smoke. The agent lists findings, fetches fix tasks and submits the new lockfile, and Smoke verifies the fix.',
    serverTitle: 'Smoke MCP server',
    serverUrl: 'Server URL',
    authBody:
      'Agents sign in with OAuth: the first time, your browser opens Smoke to approve access for a workspace. The snippets contain no secrets.',
    asset: 'Asset for this repository',
    assetHint: 'Written to .smoke/project.json so the agent knows which inventory to update.',
    chooseAsset: 'Choose an asset',
    noAssets: 'Add an asset to this project to get a .smoke/project.json file.',
    projectFileTitle: '.smoke/project.json',
    projectFileBody:
      'Commit this file to the repository. It holds only the workspace and asset IDs.',
    agents: 'Coding agents',
    installInCursor: 'Add to Cursor',
    installHint: 'Opens Cursor and installs the Smoke MCP server.',
    command: 'Command',
    files: 'Files',
    copyFile: 'Copy file',
    next: 'Then',
    tokenAlternative: 'Use an API token instead of OAuth',
    tokenAlternativeBody:
      'For clients without OAuth support. Create a personal token under API tokens and put it in the SMOKE_TOKEN environment variable; never commit it.',
    templateVersion: 'Template {{version}}',
    message: {
      claude_code: {
        authenticate:
          'In Claude Code, run {{command}}, select “{{server}}” and choose Authenticate. Sign in to Smoke in the browser and choose the workspace.',
        run: 'Then type {{command}}.',
      },
      cursor: {
        sign_in:
          'Use “Add to Cursor” (or add {{file}}), then sign in to Smoke when Cursor asks. Settings → MCP shows “Needs login” until then.',
        run: 'In the agent chat, mention {{mention}}.',
      },
      github_copilot: {
        trust_and_sign_in:
          'On first start, VS Code asks you to trust the server and to sign in to Smoke (Agent mode).',
        run: 'In Copilot Chat (Agent mode), type {{command}}.',
      },
      codex: {
        login: 'Run {{command}} and sign in to Smoke in the browser.',
        project_config: 'A trusted project may use {{projectFile}} instead of {{userFile}}.',
      },
    },
  },
  setupMode: {
    create: 'New file',
    merge: 'Merge into the existing file',
  },
  audit: {
    title: 'Audit log',
    description: 'Security-relevant actions in this workspace.',
    time: 'Time',
    action: 'Action',
    actor: 'Actor',
    target: 'Target',
    reason: 'Reason',
    emptyTitle: 'No events yet',
  },
  assignments: {
    title: 'Assigned members',
    members: 'Members',
    clientTitle: 'Members assigned to {{name}}',
    clientHelp:
      'Assigned members get this client’s notifications for all of its projects. With nobody assigned, Owners and Admins get them.',
    projectHelp:
      'Assigned members get this project’s notifications, in addition to the members assigned to its client. With nobody assigned, Owners and Admins get them.',
    none: 'Nobody is assigned.',
    since: 'since {{date}}',
    add: 'Assign a member',
    choose: 'Choose a member',
    assign: 'Assign',
    removeFor: 'Remove {{name}}',
  },
  notifications: {
    title: 'Notifications',
    description:
      'Where and when Smoke tells you about findings, SLA deadlines, fixes and mitigations.',
    tab: {
      mine: 'My notifications',
      workspace: 'Workspace',
      history: 'Delivery history',
    },
    chatsTitle: 'Linked accounts',
    chatsBody: 'Chat links belong to you and apply to all your workspaces.',
    active: 'Active',
    emailUnverified: 'not verified',
    linked: 'Linked',
    notLinked: 'not linked',
    notConfigured: 'Not available yet',
    notConfiguredBody:
      'This Smoke server is not connected to this chat service yet. Email notifications work as usual.',
    linkedSince: 'Linked on {{date}}.',
    unlink: 'Unlink',
    unlinked: 'Unlinked.',
    lineStep1: 'Add Smoke’s LINE official account as a friend (button or QR code).',
    lineStep2: 'Smoke sends you a link in LINE. Open it, sign in here and confirm.',
    lineAddFriend: 'Add Smoke on LINE',
    lineQr: 'QR code to add Smoke on LINE',
    lineNoFriendUrl: 'Search for Smoke’s official account in LINE and add it as a friend.',
    telegramIntro: 'Get a one-time link that opens a chat with Smoke’s Telegram bot.',
    telegramLink: 'Link Telegram',
    telegramOpen: 'Open the link in Telegram and press Start in the chat with @{{bot}}.',
    telegramOpenButton: 'Open Telegram',
    telegramQr: 'QR code of the Telegram link',
    copyLink: 'Copy link',
    expiresAt: 'The link works once and expires at {{time}}.',
    waiting: 'This page updates when the link is used.',
    newLink: 'Get a new link',
    mineTitle: 'My notifications in this workspace',
    mineBody:
      'Rows marked Default follow the workspace defaults; change a row to set your own. Recipients are the assignee and the members assigned to the project or client.',
    byPriority: 'Channels and timing per priority',
    timing: 'When',
    timingFor: 'When to notify about {{priority}}',
    channelFor: '{{channel}} for {{priority}}',
    source: 'Default or custom',
    custom: 'Custom',
    default: 'Default',
    notOnPlan: 'not on your plan',
    planChannels: 'Your plan sends email only. P1 alerts are always sent immediately.',
    otherTitle: 'Other notifications',
    emailTitle: 'Emails',
    emailHelp: 'Clear a box to stop that kind of email. Every email also has an unsubscribe link.',
    save: 'Save',
    saved: 'Notification settings saved.',
    resetDefaults: 'Use the workspace defaults',
    defaultsTitle: 'Workspace defaults',
    defaultsBody:
      'Used by members who have not changed a setting. Daily digests go out at {{time}} ({{timeZone}}), weekly digests on {{day}}.',
    saveDefaults: 'Save defaults',
    defaultsSaved: 'Workspace defaults saved.',
    groupsTitle: 'Group chats',
    groupsBody:
      'Telegram groups that receive this workspace’s notifications, by the routing rules below. Messages use the language of the Admin who linked the group.',
    noGroups: 'No group chats linked yet.',
    groupNotConfigured:
      'Telegram is not connected to this Smoke server yet, so group chats cannot be linked.',
    groupLink: 'Link a Telegram group',
    groupStep1: 'Add Smoke’s bot to the group (button below, or from the group’s settings).',
    groupStep2: 'Send this command in the group:',
    groupAddBot: 'Add the bot to a group',
    untitledGroup: 'Untitled group',
    routingTitle: 'Routing to group chats',
    routingBody:
      'Send notifications about a client (or every client) to a group chat. Empty categories mean every category.',
    routingNeedsGroup: 'Link a group chat first.',
    noRoutes: 'No rules: group chats receive nothing.',
    rule: 'Rule {{n}}',
    everyClient: 'Every client',
    groupChat: 'Group chat',
    minPriority: 'Priorities',
    upTo: {
      P1: 'P1 only',
      P2: 'P1 – P2',
      P3: 'P1 – P3',
      P4: 'All (P1 – P4)',
    },
    routeCategories: 'Only these categories',
    addRule: 'Add a rule',
    saveRouting: 'Save routing',
    routingSaved: 'Routing saved.',
    historyTitle: 'Delivery history',
    historyBody: 'Every notification Smoke sent or tried to send, newest first.',
    status: 'Status',
    channel: 'Channel',
    time: 'Time',
    recipient: 'Recipient',
    what: 'About',
    formerMember: 'Former member',
    findingCount_one: '{{count}} finding',
    findingCount_other: '{{count}} findings',
    attempts_one: '{{count}} attempt',
    attempts_other: '{{count}} attempts',
    noDeliveriesTitle: 'Nothing sent yet',
    noDeliveriesBody: 'Notifications appear here when Smoke sends them.',
  },
  lineLink: {
    title: 'Link your LINE account',
    subtitle: 'Receive Smoke notifications in LINE.',
    body: 'Link the LINE account that opened this link to your Smoke account {{email}}?',
    warning:
      'Continue only if you opened this link yourself from Smoke’s LINE message. Whoever owns that LINE account will receive your notifications.',
    confirm: 'Link LINE account',
    redirecting: 'Opening LINE…',
    invalid: 'This link is not valid. Open it again from Smoke’s LINE message.',
    back: 'Go to Smoke',
  },
  unsubscribe: {
    title: 'Unsubscribe',
    body: 'Stop emails about: {{list}}?',
    bodyWorkspace: 'Stop emails about {{list}} from the workspace {{workspace}}?',
    confirm: 'Unsubscribe',
    doneTitle: 'Unsubscribed',
    doneBody:
      'You will no longer get emails about: {{list}}. Other notifications are not affected.',
    manage: 'Change all notification settings:',
    signIn: 'sign in to Smoke',
  },
  os: {
    title: 'Operating system',
    description: 'From the current inventory.',
    distro: 'Distribution',
    kernel: 'Kernel',
    packages: 'Installed packages',
    packageCount_one: '{{formatted}} package ({{manager}})',
    packageCount_other: '{{formatted}} packages ({{manager}})',
    services: 'Running services',
    serviceCount_one: '{{count}} service',
    serviceCount_other: '{{count}} services',
    collectedAt: 'Collected',
    staleTitle_one: 'The inventory was collected {{count}} day ago',
    staleTitle_other: 'The inventory was collected {{count}} days ago',
    staleBody:
      'Packages change whenever the host is updated, so the matches may be out of date. Click Collect with a command, run it again and upload the result.',
    rebootRequired: 'The host reports that a reboot is required (/var/run/reboot-required).',
    unsupportedTitle: 'This release is not supported',
    unsupportedBody: 'Smoke has no vulnerability data for {{release}}, so nothing was matched.',
    unsupportedBodyUnknown:
      'Smoke has no vulnerability data for this release, so nothing was matched.',
    supportedReleases:
      'Supported: Ubuntu 22.04, 24.04 and 26.04 LTS; Debian 12 and 13; Alpine 3.20 to 3.24.',
  },
  ciUpload: {
    title: 'CI upload',
    description:
      'GitLab CI or GitHub Actions sends the lockfiles to Smoke after every push, so nobody uploads them by hand. Follow the four steps below; everything is ready to copy and paste.',
    open: 'Set up CI upload',
    openAgain: 'View setup steps',
    notSetUp: 'This asset has not received a CI upload yet.',
    stateOn: 'Set up',
    stateWaiting: 'Waiting for the first upload',
    stateNoToken: 'Token no longer valid',
    listBadge: '{{ci}}: {{state}}',
    noActiveToken:
      'This asset has no usable upload token (expired or revoked), so CI uploads are refused. Create a new token and update SMOKE_TOKEN in the CI.',
    expiringSoon:
      'The token “{{name}}” expires on {{date}}. Create a new one and update SMOKE_TOKEN in the CI first, then revoke the old one.',
    lastUpload: 'Last CI upload',
    lastUploadValue: '{{date}}, from {{source}}',
    noUploadYet: 'None yet. After a push, check that the pipeline or workflow succeeded.',
    tokens: 'Upload tokens',
    tokenExpires: 'expires {{date}}',
    tokenUsed: 'last used {{date}}',
    tokenUnused: 'not used yet',
    provider: 'CI',
    step1: 'Create an upload token for this asset',
    createToken: 'Create token',
    existing:
      'This asset already has a “{{name}}” token, expiring {{date}}. A new one does not affect it; revoke the old one under API tokens once switched.',
    adminOnly: 'Only admins create tokens. Ask an admin to do this step.',
    tokenTitle: 'The token is shown only once',
    key: 'Variable',
    value: 'Value',
    tokenPending: 'Appears here after step 1',
    branch: 'Branch to upload',
    branchHint: 'The branch you deploy; empty means the default branch.',
    badBranch: 'Branch names may only use letters, digits and . _ / @ + -',
    paths: 'Lockfile paths (one per line)',
    pathsHint:
      'Relative to the repository root. List all of them every time: a file left out counts as removed.',
    pathsFromSnapshot:
      'Filled in from the latest upload. List all of them every time: a file left out counts as removed.',
    badPaths: 'These paths have unsupported characters: {{paths}}',
    step4: 'Check after a push',
    manageTokens: 'Manage API tokens',
    gitlab: {
      tokenNote:
        'Copy it straight into the GitLab variable in the next step and do not keep it elsewhere. It can only upload this asset’s lockfiles and expires in a year.',
      step2: 'In the GitLab project, add two variables under Settings → CI/CD → Variables',
      flags: 'Options',
      urlFlag: '—',
      tokenFlag: 'Tick Masked',
      variablesHint:
        'If you also tick Protected, the branch below must be a protected branch, or the job cannot read the token.',
      step3: 'Add this job to .gitlab-ci.yml at the root of the repository',
      verify:
        'When the pipeline has run, the asset’s snapshots show a new one from “GitLab CI”. On failure the job prints the error Smoke returned.',
    },
    github: {
      tokenNote:
        'Copy it straight into the GitHub secret in the next step and do not keep it elsewhere. It can only upload this asset’s lockfiles and expires in a year.',
      step2:
        'In the GitHub repository, add two settings under Settings → Secrets and variables → Actions',
      flags: 'Tab',
      urlFlag: 'Variables',
      tokenFlag: 'Secrets',
      variablesHint: 'The token must go under Secrets: variable values show in the run logs.',
      step3: 'Save this file in the repository as .github/workflows/smoke-inventory.yml',
      verify:
        'When the workflow has run (GitHub’s Actions tab), the asset’s snapshots show a new one from “GitHub Actions”. On failure the step prints the error Smoke returned.',
    },
  },
  collector: {
    title: 'Linux collector',
    description:
      'Uploads this host’s package list once a day, so you no longer run the command by hand.',
    notInstalled: 'The collector is not installed on this host.',
    adminOnly: 'An Admin of this workspace can install it.',
    unpublishedTitle: 'The collector has not been published yet',
    unpublished:
      'This Smoke site does not offer the collector for download yet. Collect with a command for now.',
    checkFailed: 'Could not check whether the collector can be downloaded. Try again later.',
    install: 'Install the collector',
    reinstall: 'New token and install command',
    tokenTitle: 'Copy the token now. It is shown only this once.',
    tokenNote:
      'An asset token for this host only, which can only upload its package list. It expires in one year.',
    step1: 'Copy the token.',
    step2: 'Run the install command below on the host, and paste the token when it asks.',
    step3:
      'The collector reports once right away, then once a day. Reload this page to see its status.',
    installIntro:
      'Run it as a user who can use sudo. The script checks the signature of the download before installing, and reads the token from the terminal, so it does not appear on screen or in your shell history.',
    dryRun: 'To see exactly what it uploads, without sending anything:',
    uninstall: 'To remove the collector, its settings and its timer:',
    done: 'Done',
    lastReport: 'Last report',
    version: 'Version',
    versionValue: '{{version}} ({{arch}})',
    unknown: 'unknown',
    tokenExpires: 'Token expires',
    noActiveToken:
      'This host has no valid collector token, so the collector cannot upload. Create a new token and run the install command again.',
    revoke: 'Revoke or manage tokens',
    staleTitle_one: 'The collector has not reported for {{count}} day',
    staleTitle_other: 'The collector has not reported for {{count}} days',
    staleBody:
      'Check that smoke-collector.timer (or the cron job) still runs on the host and that the host can reach Smoke. Running smoke-collector run on the host shows the error. Admins were notified.',
    staleBadge_one: 'Collector: no report for {{count}} day',
    staleBadge_other: 'Collector: no report for {{count}} days',
  },
  osPaste: {
    open: 'Collect with a command',
    title: 'Collect with a command',
    hostDescription:
      'Run the command on the host; it saves the result to a file. Upload that file or paste its content.',
    imageDescription:
      'Run the command against the image; it saves the result to a file. Upload that file or paste its content.',
    method: 'How to collect',
    methodRun: 'Run the command in the image',
    methodSbom: 'Use an SBOM instead',
    sbomIntro:
      'Create a CycloneDX SBOM of the image with syft or trivy. Smoke reads the deb and apk packages in it.',
    sbomUpload: 'The command saves the SBOM as {{file}}. Upload that file:',
    chooseSbom: 'Choose the SBOM file and upload',
    uploading: 'Uploading…',
    runOnHost: '1. Run on the host. The result is saved as {{file}} in the current directory:',
    runInImage:
      '1. Run against the image (needs Docker). The result is saved as {{file}} in the current directory:',
    thenUpload: '2. Choose {{file}}, or paste its content below (cat {{file}} prints it):',
    chooseFile: 'Choose {{file}}',
    imageHint: 'Replace {{placeholder}} with the image name and tag, for example nginx:1.27.',
    imagePlaceholder: '<image>',
    copyCommand: 'Copy command',
    collectsTitle: 'What the command collects',
    collects: {
      osRelease: 'ID and VERSION_ID from /etc/os-release (distribution and release)',
      packages:
        'Installed packages: name, version, architecture, and source package name and version',
      kernel: 'The kernel version (uname -r)',
      services: 'Names of running systemd services (to say what to restart)',
      servicePackages:
        'On Ubuntu and Debian hosts, which packages each running service has loaded (to name exactly what to restart; with sudo it can read most services)',
      reboot: 'Whether /var/run/reboot-required exists',
    },
    nothingElse:
      'To find the packages a service loaded, it reads the processes’ /proc entries and the packages’ file lists, but writes only package names. It reads no other file contents. The result is plain text: you can read it before uploading.',
    output: 'File content',
    outputHint:
      'A chosen file is shown here; you can also paste the whole content of {{file}}. Up to 5 MB.',
    wrongStart:
      'The content must start with the line {{header}}. Use the whole file the command saved.',
    tooLarge: 'Larger than 5 MB.',
    submit: 'Upload',
  },
  distro: {
    kernelState: {
      oldTitle: 'This old kernel is not running',
      oldBody:
        'The host runs {{running}}. This version’s vulnerabilities only matter if the host boots into it, so it is always P4, even in KEV. Remove it; there is no need to upgrade it.',
      newerTitle: 'This newer kernel is not running yet',
      newerBody:
        'The host runs {{running}} and uses this version after a reboot. Until then it is P4; after the reboot, upload the inventory again and it is recalculated from its vulnerabilities.',
    },
    title: 'Distribution package',
    release: 'Release',
    packageManager: 'Package manager',
    sourcePackage: 'Source package',
    priority: 'Distribution priority',
    fixedVersions: 'Fixed in',
    noFixedVersion: 'No fixed version for this release yet',
    binaries_one: '{{count}} installed package built from it',
    binaries_other: '{{count}} installed packages built from it',
    arch: 'Architecture',
    subscription: 'Ubuntu Pro subscription required',
    subscriptionBody:
      'The fixed version is only available with Ubuntu Pro (ESM). Attach the machine to Ubuntu Pro before upgrading.',
    wontFixBody:
      'The distribution will not fix this in this release. Smoke sends it in digests only. Remove the package if it is not needed, or accept the risk.',
    fixedIn: 'Fixed in',
    severityFromPriority: 'severity taken from this priority',
    trackerNotes: 'Distribution tracker notes (third-party content, not reviewed by Smoke)',
    trackerNotesLabel: 'Distribution tracker notes from a third party',
  },
  distroPriority: {
    negligible: 'Negligible',
    unimportant: 'Unimportant',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical',
  },
  prioritySource: {
    ubuntu_priority: 'Ubuntu priority',
    debian_urgency: 'Debian urgency',
  },
  distroFixState: {
    fixed: 'Fixed in this release',
    no_fix: 'No fix yet',
    wont_fix: 'Won’t fix',
  },
  distroLabel: {
    distro_wont_fix: 'Distro won’t fix',
  },
  wontFixReason: {
    ubuntu_priority_negligible: 'Ubuntu priority: negligible',
    debian_urgency_unimportant: 'Debian urgency: unimportant',
    debian_urgency_end_of_life: 'Debian: end of life for this package',
    other: 'Marked won’t fix',
  },
  priority: {
    P1: 'Urgent',
    P2: 'High',
    P3: 'Medium',
    P4: 'Low',
  },
  status: {
    open: 'Open',
    in_progress: 'In progress',
    pending_verification: 'Pending verification',
    fixed: 'Fixed',
    mitigated: 'Mitigated',
    ignored: 'Ignored',
    snoozed: 'Snoozed',
    closed: 'Closed',
  },
  fixState: {
    available: 'Fix available',
    partial: 'Partial fix',
    blocked: 'Fix blocked',
    none: 'No fix',
    unknown: 'Unknown',
  },
  slaState: {
    on_track: 'On track',
    due_soon: 'Due soon',
    overdue: 'Overdue',
    met: 'Met',
  },
  environment: {
    production: 'Production',
    staging: 'Staging',
    development: 'Development',
  },
  role: {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
  },
  ecosystem: {
    ubuntu: 'Ubuntu',
    debian: 'Debian',
    alpine: 'Alpine',
    npm: 'npm',
    pypi: 'PyPI',
    composer: 'Composer',
  },
  scope: {
    production: 'Production',
    development: 'Development',
    optional: 'Optional',
  },
  severity: {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    unknown: 'Unknown severity',
  },
  priorityRule: {
    kev: 'Listed in CISA KEV (known exploited)',
    critical_epss: 'Critical severity with EPSS ≥ 10%',
    critical: 'Critical severity',
    high_epss: 'High severity with EPSS ≥ 5%',
    high_or_medium: 'High or medium severity',
    low_or_unknown: 'Low or unknown severity',
    manual: 'Raised to P1 by an Admin',
  },
  priorityAdjustment: {
    development_scope: 'Development dependency, one level lower',
    non_production_environment: 'Staging or development project, one level lower',
    kev_floor: 'Known exploited: never below P2',
    kernel_not_running: 'This kernel is not running: always P4',
  },
  historyKind: {
    created: 'Detected',
    status_changed: 'Status changed',
    priority_changed: 'Priority changed',
    assigned: 'Assigned',
    advisories_changed: 'Advisories changed',
    fix_state_changed: 'Fix state changed',
    mitigations_applied: 'Mitigation applied',
    other: 'Updated',
  },
  statusReason: {
    member_removed: 'the member left the workspace',
    detected: 'new inventory',
    user: 'by a user',
    user_action: 'by a user',
    new_inventory_clean: 'newer inventory without the version',
    reintroduced: 'version seen again',
    advisory_withdrawn: 'advisory withdrawn',
    advisory_no_longer_matches: 'advisory no longer matches',
    advisory_reinstated: 'advisory applies again',
    new_advisory: 'new advisory',
    snooze_expired: 'snooze ended',
    ignore_expired: 'ignore expired',
    fix_available: 'a fix became available',
    snoozed: 'snoozed',
    ignored: 'ignored',
    mitigated: 'mitigated',
    match: 'matching',
    rematch: 'matching again',
    manual_priority: 'raised to P1 by an Admin',
    manual_priority_removed: 'manual P1 cancelled by an Admin',
    superseded: 'superseded',
    group_changed: 'advisory group changed',
    no_asset: 'asset removed',
    direct_all_fixed: 'every advisory has a fix',
    direct_some_fixed: 'some advisories have a fix',
  },
  actorType: {
    user: 'User',
    system: 'Smoke',
    api_token: 'API token',
    mcp: 'Coding agent (MCP)',
    staff: 'Smoke staff',
  },
  fixReason: {
    distro_fixed: 'The distribution fixes every advisory in this release.',
    distro_some_fixed: 'The distribution fixes only some advisories in this release.',
    distro_no_fix: 'The distribution has no fixed version for this release yet.',
    distro_wont_fix: 'The distribution will not fix this in this release.',
    direct_all_fixed: 'A released version fixes every advisory.',
    direct_some_fixed: 'Released versions fix only some advisories.',
    transitive_in_range: 'The parents already allow a fixed version: refresh the lockfile.',
    transitive_parent_upgrade: 'A parent package upgrade brings in a fixed version.',
    transitive_override: 'An override pins the fixed version.',
    transitive_unreachable:
      'A fixed version exists, but the parent packages do not allow it and there is no override.',
    no_fixed_version: 'No released version fixes the advisories yet.',
    no_advisories: 'No active advisories.',
    signal_not_configured: 'Fix data is not configured on this server.',
    signal_unavailable: 'Fix data is temporarily unavailable.',
    registry_not_found: 'The package registry does not know this package.',
    unknown_group: 'An advisory is not known to the fix service.',
    invalid_version: 'The installed version could not be parsed.',
    too_many_advisories: 'Too many advisories to compute a common fix.',
    private_not_sent: 'A private package: not looked up on the public registry (asset setting).',
    available: 'A fix is available.',
    partial: 'A partial fix is available.',
    blocked: 'The fix is blocked.',
    none: 'No fix is available.',
    unknown: 'Not determined yet.',
  },
  packageManager: {
    dpkg: 'dpkg (apt)',
    apk: 'apk',
    npm: 'npm',
    pnpm: 'pnpm',
    yarn: 'Yarn',
    yarn_classic: 'Yarn classic',
    yarn_berry: 'Yarn Berry',
    poetry: 'Poetry',
    uv: 'uv',
    pipenv: 'Pipenv',
    pip: 'pip',
    composer: 'Composer',
    unknown: 'unknown',
  },
  semverDelta: {
    patch: 'Patch',
    minor: 'Minor',
    major: 'Major',
    unknown: 'Unknown',
  },
  strategy: {
    upgrade_os_package: 'Upgrade the OS package',
    remove_old_kernel: 'Remove the old kernel',
    reboot_new_kernel: 'Reboot into the new kernel',
    rebuild_image: 'Rebuild the image',
    upgrade_direct: 'Upgrade',
    upgrade_parent: 'Upgrade the parent package',
    override_transitive: 'Override the transitive version',
    update_in_range: 'Refresh within the current range',
    mitigate_only: 'Mitigate only',
  },
  targetReason: {
    distro_fixed_version: 'The distribution’s fixed version for this release.',
    kernel_not_running: 'This kernel is installed but not running.',
    smallest_delta_fixing_all: 'The smallest upgrade that fixes every advisory.',
    smallest_delta_fixing_covered: 'The smallest upgrade that fixes the advisories with a fix.',
    only_deprecated_candidates: 'Only deprecated versions fix these advisories.',
    within_parent_range: 'The parent packages already allow a fixed version.',
    parent_upgrade_small: 'A small parent upgrade brings in the fix.',
    override_smaller_than_parent: 'An override is a smaller change than upgrading the parent.',
    parent_upgrade_no_override: 'The package manager has no override, so the parent is upgraded.',
    no_reachable_fix: 'No fixed version can be reached from this project.',
    no_fixed_version: 'No fixed version exists yet.',
    alternative: 'Alternative approach.',
  },
  aiStatus: {
    ready: 'Ready',
    queued: 'Preparing',
    unavailable: 'Unavailable',
    disabled: 'Off',
    not_applicable: 'Not needed',
    not_generated: 'Not generated',
    outdated: 'Out of date',
  },
  aiStale: {
    fix_changed: 'The fix information changed after it was generated.',
    advisory_changed: 'The advisory changed after it was generated.',
    stack_changed: 'The stack changed after it was generated.',
  },
  aiReason: {
    in_range_update_only:
      'Regenerating the lockfile within the current ranges fixes it; no analysis is needed.',
    container_base_image_unknown:
      'Smoke cannot tell this image’s base image. Enter it under Edit on the asset page, then generate the AI analysis.',
    kernel_not_running: 'This kernel is not running; the steps above are complete without AI.',
    private_package: 'Private packages are not sent for AI analysis.',
    no_shareable_path: 'The dependency paths contain private packages, so nothing can be sent.',
    invalid_package: 'The package name or version cannot be sent for analysis.',
    linux_not_enabled: 'AI analysis for Linux packages is not switched on.',
    fix_state_none: 'There is no fix to analyze; see the mitigations.',
    fix_state_unknown: 'The fix state is not known yet.',
    finding_resolved: 'The finding is resolved.',
    not_applicable: 'Not needed for this finding.',
    update_in_range: 'A lockfile refresh within the current ranges fixes it; no analysis needed.',
  },
  restartLevel: {
    none: 'No restart',
    service_restart: 'Restart services',
    reboot: 'Reboot',
    unknown: 'Unknown',
  },
  riskLevel: {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    unknown: 'Unknown',
  },
  ratingReason: {
    wrong_target: 'Wrong target version',
    missed_breaking_change: 'Missed a breaking change',
    steps_did_not_work: 'The steps did not work',
    unclear: 'Unclear',
    not_applicable: 'Does not apply to us',
    other: 'Other',
  },
  mitigationKind: {
    disable_feature: 'Disable a feature',
    config_change: 'Configuration change',
    restrict_input: 'Restrict input',
    restrict_network_access: 'Restrict network access',
    isolate_host: 'Isolate the host',
    stop_service: 'Stop the service',
    waf_rule: 'WAF / reverse proxy',
    least_privilege: 'Least privilege',
    remove_component: 'Remove the component',
    monitor_detect: 'Monitor and detect',
    replace_package: 'Replace the package',
    other: 'Other',
  },
  mitigationRef: {
    generic: 'Generic mitigation',
    ai: 'AI suggestion',
    sourced: 'Sourced mitigation',
    other: 'Other mitigation',
  },
  effectiveness: {
    blocks_exploit: 'Blocks the exploit',
    reduces_risk: 'Reduces risk',
    detection_only: 'Detection only',
  },
  impact: {
    low: 'low',
    medium: 'medium',
    high: 'high',
    service_outage: 'service outage',
  },
  ignoreReason: {
    not_affected: 'Not affected',
    accepted_risk: 'Accepted risk',
    false_positive: 'False positive',
  },
  ignoreReasonHelp: {
    not_affected: 'The vulnerable code is not used or not reachable.',
    accepted_risk: 'We accept the risk for now. Meets a mitigate-or-accept SLA.',
    false_positive: 'The match is wrong. Also consider reporting it.',
  },
  advisoryState: {
    active: 'Active',
    withdrawn: 'Withdrawn',
    not_matching: 'No longer matches',
  },
  referenceType: {
    ADVISORY: 'Advisory',
    ARTICLE: 'Article',
    DETECTION: 'Detection',
    DISCUSSION: 'Discussion',
    EVIDENCE: 'Evidence',
    FIX: 'Fix',
    INTRODUCED: 'Introduced',
    PACKAGE: 'Package',
    REPORT: 'Report',
    WEB: 'Web',
  },
  matchStatus: {
    pending: 'Matching…',
    matched: 'Matched',
    failed: 'Failed',
  },
  format: {
    'smoke-host': 'Host command output',
    'npm-package-lock': 'npm lockfile',
    'pnpm-lock': 'pnpm lockfile',
    'yarn-classic': 'Yarn classic lockfile',
    'yarn-berry': 'Yarn Berry lockfile',
    'poetry-lock': 'Poetry lockfile',
    'uv-lock': 'uv lockfile',
    'pipfile-lock': 'Pipfile.lock',
    'requirements-txt': 'requirements.txt',
    'composer-lock': 'Composer lockfile',
    'package-json': 'package.json',
    'cyclonedx-json': 'CycloneDX SBOM',
    'spdx-json': 'SPDX SBOM',
  },
  parseNote: {
    os_inventory:
      '{{distro}} {{release}}: {{packages}} installed packages from {{sourcePackages}} source packages.',
    os_packages_not_read:
      'The OS packages in this SBOM were not read: {{reason}} Its application packages were read.',
    scope_unknown:
      'The file does not say which packages are for development; all are treated as production.',
    direct_inferred:
      'The file does not list the project’s own dependencies; packages nothing else depends on are treated as direct.',
    no_dependency_graph: 'The file has no dependency graph, so dependency paths are not available.',
    unreachable_packages:
      '{{count}} packages are not reachable from the project’s dependencies; they are listed without dependency paths.',
    companion_manifest: 'Read together with the lockfile next to it.',
    scope_from_file_name:
      'The scope was taken from the file name (for example requirements-dev.txt).',
  },
  parseError: {
    host_asset_required:
      'Command output can only be added to a Linux host or container image asset. Create one on the project page.',
    os_inventory_required:
      'A host or container image needs the file the command saved, or an SBOM. Upload lockfiles to an application asset: here they would replace the OS package list of this machine.',
    multiple_os_inventories:
      'An upload can hold the OS packages of one host or image only. Upload each host to its own asset.',
    unsupported_os: 'Only Ubuntu, Debian and Alpine are supported.',
    malformed_host_inventory:
      'The command output could not be read. Run the command again and paste everything it printed.',
    unsupported_file: 'Not a supported manifest or lockfile.',
    unsupported_version:
      'This lockfile version is not supported (npm lockfile v1, for example). Regenerate it with a current package manager.',
    malformed_json: 'Not valid JSON.',
    malformed_yaml: 'Not valid YAML.',
    malformed_toml: 'Not valid TOML.',
    malformed_lockfile: 'The lockfile could not be read.',
    invalid_encoding: 'Not UTF-8 text.',
    invalid_path: 'The path is not valid.',
    duplicate_path: 'Two files have the same path.',
    file_too_large: 'Larger than {{maxMb}} MB.',
    too_many_files: 'More than {{maxFiles}} files.',
    total_too_large: 'Larger than {{maxMb}} MB together.',
  },
  skipReason: {
    workspace: 'Workspace package (part of this repository)',
    local: 'Local path',
    git: 'Installed from Git',
    link: 'Linked package',
    non_registry: 'Not from a registry (URL or path)',
    alias_unresolved: 'Alias could not be resolved',
    malformed: 'Malformed entry',
    invalid_name: 'Invalid package name',
    unsupported_ecosystem: 'Ecosystem not supported',
    extraneous: 'Extraneous (not required by the project)',
  },
  unresolvedReason: {
    not_pinned:
      'Not pinned with ==. Pin the version, or upload a lockfile (poetry.lock, uv.lock, Pipfile.lock) or pip freeze output.',
    include: 'Includes another requirements file: upload that file as well.',
    unpinned_manifest: 'A manifest range, not an installed version: upload the lockfile.',
    no_purl: 'The SBOM entry has no package URL.',
    no_version: 'No version given.',
    unparseable: 'The line could not be read.',
  },
  snapshotSource: {
    upload: 'Upload',
    mcp: 'Coding agent (MCP)',
    collector: 'Linux collector',
    ci: 'CI',
    ci_gitlab: 'GitLab CI',
    ci_github: 'GitHub Actions',
    repository: 'GitHub',
    api: 'API',
    github: 'GitHub',
  },
  assetKind: {
    manifest_upload: 'Upload',
    repository: 'Repository',
    host: 'Server',
    container_image: 'Container image',
    database: 'Database',
  },
  eolStatus: {
    eol: 'End of life',
    eol_soon: 'Ending soon',
    supported: 'Supported',
    not_found: 'Unknown release',
    not_tracked: 'Not tracked',
    unavailable: 'Unavailable',
  },
  stackComponent: {
    runtime: 'Runtime',
    framework: 'Framework',
    os: 'Operating system',
    database: 'Database',
    service: 'Service',
  },
  stackSource: {
    detected: 'Detected',
    asset: 'Manual',
    project: 'Project level',
  },
  tokenKind: {
    personal: 'Personal',
    project: 'Project',
    asset: 'Asset',
    oauth: 'OAuth (coding agent)',
  },
  tokenScope: {
    'findings:read': 'Read findings',
    'findings:write': 'Update findings',
    'inventory:write': 'Submit inventories',
  },
  auditAction: {
    'client.created': 'Client created',
    'client.archived': 'Client archived',
    'client.unarchived': 'Client unarchived',
    'client.deleted': 'Client deleted',
    'project.created': 'Project created',
    'project.archived': 'Project archived',
    'project.unarchived': 'Project unarchived',
    'project.deleted': 'Project deleted',
    'asset.created': 'Asset created',
    'asset.updated': 'Asset updated',
    'asset.private_registry_lookup_changed': 'Private package lookup setting changed',
    'asset.archived': 'Asset archived',
    'asset.unarchived': 'Asset unarchived',
    'asset.deleted': 'Asset deleted',
    'assignment.added': 'Member assigned',
    'assignment.removed': 'Assignment removed',
    'api_token.created': 'API token created',
    'api_token.revoked': 'API token revoked',
    'api_token.renamed': 'API token renamed',
    'finding.false_positive_reported': 'False positive reported',
    'finding.fix_prompt_copied': 'Fix prompt copied',
    'finding.remediation_requested': 'AI analysis requested',
    'project.eol_prompt_generated': 'Upgrade prompt generated',
    'finding.mitigations_applied': 'Mitigations applied',
    'finding.remediation_rated': 'Remediation rated',
    'inventory.snapshot_created': 'Inventory uploaded',
    'invitation.created': 'Member invited',
    'invitation.revoked': 'Invitation revoked',
    'mcp.tool_called': 'MCP tool called',
    'member.joined': 'Member joined',
    'member.removed': 'Member removed',
    'member.role_changed': 'Role changed',
    'oauth.authorized': 'App authorized',
    'oauth.refresh_token_reused': 'Refresh token reuse detected',
    'workspace.created': 'Workspace created',
    'workspace.updated': 'Workspace updated',
    'workspace.deletion_requested': 'Workspace deletion requested',
    'workspace.sla_policy_changed': 'SLA policy changed',
    'asset.addresses_updated': 'Updated server IP addresses',
    'asset.stack_updated': 'Updated an asset stack',
    'asset.deployment_set': 'Set the server an asset runs on',
    'asset.deployment_removed': 'Removed the server an asset runs on',
    'advisory.manual_p1_set': 'Raised an advisory to P1',
    'advisory.manual_p1_removed': 'Cancelled a manual P1',
  },
  auditTarget: {
    workspace: 'Workspace',
    invitation: 'Invitation',
    membership: 'Member',
    api_token: 'API token',
    finding: 'Finding',
    asset: 'Asset',
    snapshot: 'Snapshot',
    client: 'Client',
    project: 'Project',
  },
  loginMethod: {
    password: 'Email and password',
    google: 'Google',
    github: 'GitHub',
  },
  channel: {
    email: 'Email',
    line: 'LINE',
    telegram: 'Telegram',
  },
  timing: {
    immediate: 'Immediately',
    daily: 'Daily digest',
    weekly: 'Weekly digest',
    off: 'Off',
  },
  notificationCategory: {
    finding_alert: 'New and higher-priority findings',
    sla_reminder: 'SLA reminders',
    fix_available: 'Fix available',
    mitigation: 'New mitigations',
    mitigation_review: 'Mitigation reviews',
    eol: 'End-of-life notices',
    collector_stale: 'Collector not reporting',
    upload_problem: 'Automatic uploads stopping',
    digest: 'Digest',
  },
  notificationCategoryHelp: {
    sla_reminder: '“Due in 2 days” and “overdue”, for findings assigned to you and for Admins.',
    fix_available: 'A finding without a usable fix gets one.',
    mitigation: 'A sourced mitigation appears for a finding without a fix.',
    mitigation_review: 'A mitigated finding reaches its review date.',
    eol: 'A runtime, framework or OS in your projects nears or reaches end of life.',
    collector_stale:
      'A host’s Linux collector has not reported for 3 days (Admins only, once until it reports again).',
    upload_problem:
      'An upload token that CI or a collector uses expires within 30 days or expired, or a tracked GitHub repository starts failing to scan (Admins only).',
  },
  emailList: {
    finding_alert: 'New and higher-priority findings',
    sla_reminder: 'SLA reminders',
    fix_available: 'Fix available',
    mitigation: 'New mitigations',
    mitigation_review: 'Mitigation reviews',
    collector_stale: 'Collector not reporting',
    upload_problem: 'Automatic uploads stopping',
    digest: 'Daily and weekly digests',
  },
  deliveryStatus: {
    queued: 'Queued',
    sent: 'Sent',
    failed: 'Failed',
    skipped: 'Skipped',
  },
  deliveryMode: {
    immediate: 'immediate',
    daily: 'daily digest',
    weekly: 'weekly digest',
  },
  deliveryError: {
    channel_unavailable: 'Chat not linked any more',
    opted_out: 'Unsubscribed',
    no_longer_applicable: 'No longer applicable',
    blocked: 'Blocked by the recipient (unlinked)',
    rate_limited: 'Rate limited',
    invalid_request: 'Rejected by the chat service',
    not_configured: 'Channel not configured',
    migrated: 'Group moved',
    transient: 'Temporary error',
  },
  unlinkReason: {
    user: 'Unlinked',
    admin: 'Unlinked by an Admin',
    blocked: 'Bot removed from the group',
    relinked: 'Replaced by a new link',
    unlinked: 'Unlinked',
  },
  weekday: {
    monday: 'Monday',
  },
  planTier: {
    free: 'Free',
    pro: 'Pro',
    agency: 'Agency',
  },
  planLimit: {
    title: 'Plan limit reached',
    projects:
      'The {{plan}} plan allows {{limit}} active projects, and {{used}} are in use. Archive a project or upgrade the plan to add more.',
    assets:
      'The {{plan}} plan allows {{limit}} active assets, and {{used}} are in use. Archive an asset or upgrade the plan to add more.',
    workspaces:
      'The {{plan}} plan pays for {{limit}} workspace, and this would make {{used}}. Upgrade the plan or delete a workspace you no longer use.',
    featureUnavailable: '{{feature}} is not part of the {{plan}} plan. Upgrade to use it.',
    feature: {
      pdfReports: 'The PDF client report',
    },
    seePlans: 'See plans and upgrade',
    chatChannels:
      'The {{plan}} plan sends notifications by email only. Upgrade to use Telegram, including groups.',
  },
  pricing: {
    title: 'Plans and pricing',
    description:
      'Prices come from the payment catalog. Matching and P1 alerts never stop on existing projects because of a limit.',
    accountDescription:
      'The plan belongs to your account and covers every workspace you are the billing owner of; its limits are totals over them. The Free plan owns one workspace.',
    paidByYou:
      'Your account’s plan pays for this workspace. Changing it applies to every workspace you are the billing owner of.',
    paidBy: 'The plan of this workspace is paid by {{name}}’s account. Ask {{name}} to change it.',
    noBillingOwner: 'This workspace has no billing owner and has the Free plan limits.',
    manage: 'Manage my plan',
    publicDescription:
      'Start free. Upgrade when you track more client projects. Prices in New Taiwan dollars.',
    monthly: 'Monthly',
    perMonth: '/ month',
    refundNote:
      'Paid plans bill monthly and renew automatically. A plan starts as soon as you pay, and payments are not refunded. Cancel any time; the plan stays until the end of the period. See the <refund>refund policy</refund>.',
    free: 'No charge',
    priceUnavailable: 'Price not available right now',
    currentPlan: 'Current plan',
    scheduled: 'Starts at period end',
    unlimited: 'unlimited',
    workspaces: '{{value}} workspace',
    workspacesShared: 'Any number of workspaces; the limits below are totals over them',
    projects: '{{value}} projects',
    assets: '{{value}} assets',
    aiCalls: '{{value}} AI remediation analyses a month',
    aiFairUse: 'AI remediation analyses (fair use)',
    notificationsFree: 'Email, weekly digest (P1 alerts immediately)',
    notificationsAll: 'Email and Telegram, any timing',
    pdfReports: 'PDF client reports',
    csvExport: 'CSV export',
    notIncluded: 'Not included:',
    getStarted: 'Get started',
    action: {
      purchase: 'Choose {{plan}}',
      upgrade: 'Upgrade to {{plan}}',
      downgrade: 'Switch to {{plan}}',
    },
  },
  billing: {
    title: 'Billing',
    description:
      'Plan, payment method, invoice details and payment history of your account. The plan covers every workspace you are the billing owner of.',
    seePlans: 'See the plans',
    changePlan: 'Change plan',
    unconfigured: 'Online payment is not set up on this Smoke server yet.',
    operationPending:
      'A payment is being confirmed. You can change the plan again once it is settled.',
    operationPendingKind: 'A payment is being confirmed ({{kind}}). This page updates by itself.',
    override: 'Smoke staff set your account to {{plan}}.',
    overrideUntil: 'Smoke staff set your account to {{plan}} until {{date}}.',
    workspaces: {
      title: 'Workspaces this plan covers',
      none: 'You are not the billing owner of any workspace.',
    },
    downgradeScheduledTitle: 'Plan change scheduled',
    downgradeScheduledBody:
      'Your plan changes to {{plan}} on {{date}}, at the end of the paid period. You keep the current plan until then.',
    downgradeScheduledShort: 'A change to {{plan}} is scheduled for the end of the period.',
    downgradeScheduledToast: 'The change to {{plan}} is scheduled for the end of the period.',
    downgradeCanceled: 'You keep the current plan.',
    keepPlan: 'Keep {{plan}}',
    dunning: {
      title: 'The last payment failed',
      body: 'We retry on {{next}} (attempt {{attempt}}). Update the card before {{grace}} to keep the plan; after that the workspace falls back to Free limits. Existing projects keep being matched.',
    },
    plan: {
      title: 'Current plan',
      subscribed: 'Paid subscription: {{plan}}',
      interval: 'Billing',
      period: 'Current period',
      renews: 'Renews on',
      ends: 'Paid until',
      credit: 'Credit',
    },
    usage: {
      title: 'Usage',
      projects: 'Projects',
      assets: 'Assets',
      aiCalls: 'AI analyses this month',
      workspaces: 'Workspaces',
      shared: 'Totals over every workspace you are the billing owner of.',
      of: '{{used}} of {{limit}}',
      fairUse: 'fair use',
    },
    card: {
      title: 'Payment method',
      summary: '{{brand}} ending in {{last4}}',
      card: 'Card',
      expires: 'Expires {{date}}',
      none: 'No card saved. Renewals need a card on file.',
      add: 'Add a card',
      replace: 'Replace card',
      note: 'Card details are entered on the payment page and never reach Smoke.',
    },
    details: {
      title: 'Billing details and e-invoice',
      description: 'Used for receipts and the Taiwan e-invoice that Aircraft Pay issues.',
      email: 'Billing email',
      emailHint: 'Receipts, invoices and payment reminders go here.',
      name: 'Billing name',
      nameHint: 'Optional: a person or company name for the payment page.',
      save: 'Save billing details',
    },
    recipient: {
      title: 'Invoice recipient',
      hint: 'How the e-invoice is delivered. Aircraft Pay (飛空艇科技股份有限公司) issues it; Smoke only passes these details.',
      type: {
        member: 'Member carrier',
        mobile: 'Mobile barcode',
        citizen: 'Citizen certificate',
        donation: 'Donate the invoice',
        b2b: 'Company (tax ID)',
      },
      typeHint: {
        member: 'Stored with your Aircraft Pay account',
        mobile: '手機條碼, e.g. /ABC1234',
        citizen: '自然人憑證, 2 letters and 14 digits',
        donation: '愛心碼 of a charity',
        b2b: '統一編號 and company name',
      },
      mobile: 'Mobile barcode',
      mobileHint: '"/" followed by 7 characters: digits, capital letters, ".", "+" or "-".',
      citizen: 'Citizen certificate number',
      citizenHint: '2 capital letters followed by 14 digits.',
      donation: 'Donation code',
      donationHint: '3 to 7 digits.',
      taxId: 'Tax ID (統一編號)',
      taxIdHint: '8 digits.',
      companyName: 'Company name',
      errors: {
        required: 'Required',
        mobileFormat: 'Use "/" followed by 7 characters (0–9, A–Z, ".", "+", "-").',
        citizenFormat: 'Use 2 capital letters followed by 14 digits.',
        donationFormat: 'Use 3 to 7 digits.',
        taxIdFormat: 'Use exactly 8 digits.',
        taxIdChecksum: 'This tax ID is not valid. Check the digits.',
        companyNameLength: 'Use at most 60 characters.',
      },
    },
    history: {
      title: 'Payment history',
      empty: 'No payments yet',
      date: 'Date',
      item: 'Item',
      amount: 'Amount',
      status: 'Status',
      invoice: 'Invoice number',
      mock: 'Mock',
      noInvoiceMock: 'No invoice (mock payment)',
      invoicePending: 'Pending',
      refunded: '{{amount}} refunded',
    },
    change: {
      purchaseTitle: 'Choose {{plan}}',
      upgradeTitle: 'Upgrade to {{plan}}',
      downgradeTitle: 'Switch to {{plan}}',
      purchaseBody:
        '{{plan}} costs {{price}} {{interval}}. The plan starts as soon as the payment is confirmed and renews every month until you cancel.',
      consent:
        'I have read and agree to the <terms>terms of service</terms> and the <refund>refund policy</refund>. I agree that the service starts as soon as I pay, and I understand that payments are not refunded.',
      creditApplies: 'Your credit of {{amount}} is applied first.',
      checkoutNote: 'You enter the card on the secure payment page; it is saved for renewals.',
      chargedToCard: 'The amount is charged to the saved card.',
      upgradeSamePeriod:
        'You pay the prorated difference for the rest of the current period. {{plan}} starts now; the period still ends on {{end}}.',
      quoteCaption: 'Amount due for the upgrade',
      downgradeBody:
        'Your plan changes to {{plan}} on {{date}}, at the end of the paid period. Nothing is refunded for the rest of the period.',
      downgradeBodyNoDate: 'Your plan changes to {{plan}} at the end of the paid period.',
      confirmPay: 'Continue: pay {{amount}}',
      confirmDowngrade: 'Schedule the change',
    },
    quote: {
      proratedDifference: 'Prorated difference',
      creditApplied: 'Credit',
      dueNow: 'Due now',
    },
    return: {
      confirmingTitle: 'Confirming the payment…',
      confirmingBody:
        'We are waiting for the payment provider to confirm. The plan changes only after that; this page updates by itself.',
      slowTitle: 'Still waiting for the confirmation',
      slowBody:
        'The payment provider has not confirmed yet. You do not need to pay again: the plan changes as soon as the confirmation arrives, and we email the Owners.',
      doneTitle: 'Billing updated',
      planNow: 'Your plan is now {{plan}}.',
      cardSaved: 'Your payment method is saved.',
      canceledTitle: 'Payment canceled',
      canceledBody: 'Nothing was charged. Your plan has not changed.',
      expiredBody: 'The checkout page expired before the payment was made. Nothing was charged.',
      declinedTitle: 'Payment declined',
      declinedBody:
        'The payment did not go through. Nothing was charged and your plan has not changed.',
      cardDeclinedBody: 'The card could not be saved. Nothing was charged.',
      reasonLabel: 'Reason',
      reason: {
        card_declined: 'The card was declined by the bank.',
        insufficient_funds: 'Insufficient funds.',
        expired_card: 'The card has expired.',
        incorrect_cvc: 'The security code was incorrect.',
        processing_error: 'The payment provider could not process the card.',
        payment_failed: 'The payment failed.',
        payment_method_required: 'No saved card is available.',
        checkout_expired: 'The checkout page expired.',
      },
      tryAgain: 'Choose a plan again',
      notFound: 'This order was not found.',
    },
    status: {
      active: 'Active',
      past_due: 'Payment overdue',
    },
    orderKind: {
      purchase: 'purchase',
      renewal: 'renewal',
      upgrade: 'upgrade',
      card_setup: 'card setup',
    },
    itemType: {
      plan: 'Plan',
    },
    paymentStatus: {
      succeeded: 'Paid',
      pending: 'Pending',
      processing: 'Processing',
      failed: 'Failed',
      refunded: 'Refunded',
      partially_refunded: 'Partly refunded',
      canceled: 'Canceled',
    },
    failure: {
      PAYMENT_FAILED: 'Card declined',
      PAYMENT_METHOD_REQUIRED: 'No card on file',
      GATEWAY_ERROR: 'Outcome unknown',
      MAINTENANCE: 'Provider under maintenance',
      MERCHANT_NOT_ROUTED: 'Payment routing problem',
    },
  },
  reports: {
    title: 'Reports and exports',
    description:
      'Client reports (PDF) and CSV exports are created in the background. Download links expire; open the list again for a fresh one.',
    adminOnly: 'Only Owners and Admins can create reports and exports.',
    clientTitle: 'Client report (PDF)',
    clientDescription:
      'Summary by priority, SLA compliance, EOL notices and fixed items for one client and period.',
    client: 'Client',
    noClient: 'Projects without a client',
    from: 'From',
    to: 'To',
    language: 'Report language',
    timeZoneHint: 'Dates are calendar days in the workspace time zone ({{timeZone}}).',
    generate: 'Generate report',
    periodError: {
      required: 'Choose both dates.',
      order: 'The end date is before the start date.',
      tooLong: 'A report covers at most 366 days.',
    },
    exportTitle: 'CSV export',
    exportDescription: 'Findings or decisions (with VEX status) as a UTF-8 spreadsheet file.',
    exportKind: 'Export',
    scope: 'Scope',
    allProjects: 'All projects',
    clients: 'Clients',
    projects: 'Projects',
    exportAudited: 'Every export is recorded in the audit log.',
    export: 'Create CSV',
    queued: 'Queued. The file appears in the list when it is ready.',
    listTitle: 'Recent reports and exports',
    emptyTitle: 'No reports yet',
    emptyBody: 'Generate a client report or a CSV export above.',
    columns: {
      kind: 'Type',
      scope: 'Client or scope',
      status: 'Status',
      created: 'Created',
    },
    kind: {
      client_pdf: 'Client report (PDF)',
      findings_csv: 'Findings (CSV)',
      decisions_csv: 'Decisions (CSV)',
    },
    status: {
      queued: 'Queued',
      running: 'Generating',
      ready: 'Ready',
      failed: 'Failed',
      expired: 'Expired',
    },
    error: {
      render_failed: 'The file could not be created.',
      CLIENT_NOT_FOUND: 'The client no longer exists.',
    },
    rows_one: '{{count}} row',
    rows_other: '{{count}} rows',
    working: 'Working…',
    download: 'Download',
    availableUntil: 'Kept until {{date}}',
  },
  github: {
    title: 'GitHub',
    description:
      'Connect repositories through the Smoke GitHub App (read-only access to contents and metadata). Smoke scans the chosen manifests on push, daily, and when you ask.',
    adminOnly: 'Only Owners and Admins can manage the GitHub connection.',
    connect: 'Connect GitHub',
    connectAnother: 'Connect another account',
    notConfiguredTitle: 'GitHub is not set up on this Smoke server',
    notConfiguredBody:
      'The Smoke GitHub App has no credentials on this server yet, so repositories cannot be connected. You can still upload lockfiles and manifests to an asset by hand.',
    uploadInstead: 'Upload manifests instead',
    noInstallationsTitle: 'No GitHub account connected',
    noInstallationsBody:
      'Install the Smoke GitHub App on your account or organization and choose the repositories it may read.',
    installations: 'Connected accounts',
    suspended: 'Suspended',
    addRepository: 'Add repository',
    repositories: 'Tracked repositories',
    noRepositoriesTitle: 'No repositories tracked yet',
    noRepositoriesBody: 'Add a repository to a project, then choose its branch and manifests.',
    setupFailed: 'GitHub could not be connected',
    requested:
      'The installation was requested. An owner of the GitHub organization has to approve it.',
    connected: 'GitHub is connected. Add the repositories to track.',
    branch: 'Branch',
    defaultBranch: 'Default branch: {{branch}}',
    manifestCount_one: '{{count}} manifest',
    manifestCount_other: '{{count}} manifests',
    accessLost: 'Access lost',
    configure: 'Branch and manifests',
    rescan: 'Rescan now',
    scanQueued: 'Scan queued.',
    untrack: 'Stop tracking',
    confirmUntrack: 'Stop tracking {{repo}}? The asset and its snapshots stay.',
    lastScan: 'Last scan',
    neverScanned: 'not scanned yet',
    chooseManifests: 'Choose the manifests to track before the first scan.',
    addTitle: 'Add a repository from {{account}}',
    project: 'Project',
    projectHint: 'The repository becomes an asset of this project (it counts toward the plan).',
    filter: 'Filter repositories',
    noAvailable: 'No repositories match.',
    private: 'Private',
    alreadyTracked: 'Tracked',
    track: 'Track repository',
    tracked:
      'Repository added. Smoke scans the manifests found on the default branch; use “Branch and manifests” to change them.',
    configureTitle: 'Branch and manifests of {{repo}}',
    discover: 'Find manifests',
    manifests: 'Manifests to scan',
    manifestsHint:
      'Supported lockfiles and manifests on the branch (vendored directories are skipped).',
    noManifests: 'No supported manifests found yet. Choose the branch and select "Find manifests".',
    truncated: 'The repository is large; only part of it was searched.',
    accountType: {
      User: 'Personal account',
      Organization: 'Organization',
    },
    selection: {
      all: 'All repositories',
      selected: 'Selected repositories',
    },
    setupError: {
      state_invalid: 'The setup link expired or belongs to another user. Start again.',
      forbidden: 'Only Owners and Admins can connect GitHub.',
      installation_invalid: 'GitHub did not return a valid installation. Start again.',
      installation_not_accessible: 'Your GitHub user cannot access this installation.',
      github_unavailable: 'GitHub could not be reached. Try again later.',
    },
    scanStatus: {
      ok: 'Scanned',
      unchanged: 'No changes',
      skipped: 'Already scanned',
      failed: 'Failed',
    },
    scanError: {
      installation_unavailable: 'The GitHub App can no longer read this repository.',
      no_manifests: 'None of the chosen manifests exist on the branch.',
      branch_not_found: 'The branch does not exist.',
      file_too_large: 'A manifest is too large.',
      github_error: 'GitHub returned an error.',
      inventory_lockfile_required:
        'Only package.json was found: commit a lockfile (package-lock.json, pnpm-lock.yaml or yarn.lock).',
      inventory_parse_failed: 'A manifest could not be parsed.',
    },
  },
  staff: {
    badge: 'Staff',
    nav: 'Staff console',
    backToApp: 'Back to Smoke',
    notStaff: 'This area is for Smoke staff only.',
    twoFactorTitle: 'Two-factor authentication required',
    twoFactorBody: 'Smoke staff must use two-factor authentication. Turn it on, then come back.',
    twoFactorLink: 'Open account settings',
    adminOnly: 'Changes here need the staff admin role.',
    reason: 'Reason',
    reasonHint: 'At least {{min}} characters. Recorded in the audit log.',
    reasonAudited: 'The reason is recorded in the workspace audit log.',
    role: {
      support: 'Support',
      admin: 'Staff admin',
    },
    tabs: {
      health: 'Sync health',
      falsePositives: 'False positives',
      mitigationReviews: 'Mitigation review',
      aiUsage: 'AI usage',
      workspaces: 'Workspaces',
      flags: 'Feature flags',
      alerts: 'Alerts',
      users: 'Staff',
    },
    health: {
      title: 'Advisory source sync health',
      description: 'From smoke-signal. Lag is the time since the last successful sync.',
      unavailable: 'smoke-signal is not reachable.',
      sources: 'Sources',
      dataVersion: 'Data version {{version}}',
      source: 'Source',
      lastSuccess: 'Last success',
      lag: 'Lag',
      records: 'Records (last run)',
      quarantined: 'Quarantined',
      state: 'State',
      ok: 'OK',
      disabled: 'Disabled',
      failing_one: '{{count}} failure in a row',
      failing_other: '{{count}} failures in a row',
      dropped_one: '{{count}} part dropped',
      dropped_other: '{{count}} parts dropped',
      minutes_one: '{{count}} minute',
      minutes_other: '{{count}} minutes',
      hours_one: '{{count}} hour',
      hours_other: '{{count}} hours',
    },
    fp: {
      title: 'False-positive reports',
      description:
        'Reports from customers. Accepting one records the decision for the advisory groups.',
      state: 'Review state',
      states: {
        pending: 'Pending',
        accepted: 'Accepted',
        rejected: 'Rejected',
      },
      empty: 'Nothing in this queue',
      package: 'Package',
      advisories: 'Advisory groups',
      note: 'Customer note',
      reported: 'Reported',
      accept: 'Accept',
      reject: 'Reject',
      acceptTitle: 'Accept the report for {{package}}',
      rejectTitle: 'Reject the report for {{package}}',
      reasonAudited:
        'The decision and the reason are recorded in the customer workspace audit log.',
      done: 'Decision recorded.',
    },
    mr: {
      title: 'Sourced-mitigation review',
      description:
        'AI summaries of public sources for KEV-listed or critical advisories. They are shown to customers at once, labeled as not yet reviewed; approve them or hide items that are wrong.',
      unavailable: 'smoke-signal is not reachable, so the queue cannot be shown.',
      empty: 'Nothing to review',
      subject: 'Advisory group',
      language: 'Language',
      severity: 'Severity',
      items: 'Items',
      generated: 'Generated',
      state: 'State',
      states: { unreviewed: 'Not reviewed', approved: 'Approved' },
      open: 'Review',
      back: 'Back to the queue',
      superseded: 'A newer result replaced this one. Decisions are no longer possible.',
      fixStatus: 'Fix status in the sources: {{status}}',
      officialSupport: 'stated by an official source',
      summary: 'Summary',
      conflicts: 'Sources disagree',
      sources: 'Sources',
      license: 'License: {{license}}',
      hidden: 'Hidden',
      hide: 'Hide from customers',
      approve: 'Approve',
      approveTitle: 'Approve this result',
      approveHint_zero: 'All items stay visible.',
      approveHint_one: '{{count}} selected item will be hidden at the same time.',
      approveHint_other: '{{count}} selected items will be hidden at the same time.',
      hideSelected_one: 'Hide {{count}} item',
      hideSelected_other: 'Hide {{count}} items',
      hideTitle: 'Hide the selected items',
      hideHint: 'Hidden items disappear for customers. This cannot be undone.',
      reasonAudited:
        'The decision, your name and the reason are recorded in smoke-signal and the staff log.',
      done: 'Decision recorded.',
      untrustedNote: 'Quotes and source titles are third-party text, shown as plain text.',
    },
    ai: {
      title: 'BlueMagpie usage and cost',
      description:
        'Real calls only (cache hits shown separately). Cost is an estimate from token prices.',
      calls: 'Calls',
      cacheHits: 'Cache hits',
      failures: 'Failures',
      tokens: 'Tokens',
      tokensInOut: 'Tokens in / out',
      cost: 'Cost (USD)',
      fairUse: 'fair use {{value}}',
      empty: 'No usage in this period',
    },
    ws: {
      title: 'Workspaces',
      description: 'Plans and sizes only. Open read-only access with a reason to see more.',
      search: 'Search by name or billing owner email',
      empty: 'No workspaces found',
      name: 'Workspace',
      plan: 'Plan',
      billingOwner: 'Billing owner',
      status: 'Status',
      periodEnd: 'Period ends',
      counts: 'Members · projects · assets',
      countsValue: '{{members}} · {{projects}} · {{assets}}',
      deleting: 'Deletion requested',
      overridden: 'Override',
      pending: 'Changes to {{plan}}',
      detailTitle: 'Workspace',
      overview: 'Overview',
      timeZone: 'Time zone',
      created: 'Created',
      projects: 'Projects',
      openFindings: 'Open findings',
      override: 'Override',
      members: 'Members',
    },
    access: {
      title: 'Read-only access',
      description:
        'Details, payments and the workspace itself are visible only with an access grant. The grant and its reason appear in the workspace audit log.',
      open: 'Open read-only access',
      confirm: 'Open access',
      reasonAudited: 'The customer sees this reason in their audit log.',
      minutes: 'Duration (minutes)',
      minutesHint: '5 to 1,440 minutes. Default 60.',
      activeTitle: 'Staff access: read-only',
      activeBody:
        'You are viewing this workspace as Smoke staff until {{until}}. Nothing can be changed.',
      reasonLabel: 'Reason',
      openWorkspace: 'Open the workspace',
      backToConsole: 'Back to the staff console',
      end: 'End access',
    },
    override: {
      title: 'Plan override',
      description:
        'Assign a plan by hand (trials, special agreements). Every change is audited. It applies to the billing owner’s account, so to every workspace they are the billing owner of.',
      plan: 'Plan',
      until: 'Until',
      untilHint: 'Optional. Leave empty for no end date.',
      projects: 'Project limit',
      assets: 'Asset limit',
      limitHint: 'Optional. Empty keeps the plan limit.',
      apply: 'Apply override',
      remove: 'Remove override',
    },
    credit: {
      title: 'Credit',
      description: 'Added to the next payments (NT$). Use a negative amount to remove credit.',
      amount: 'Amount (NT$)',
      amountHint: 'Whole dollars, for example 500 or -500.',
      apply: 'Adjust credit',
      confirmTitle: 'Adjust credit by {{amount}}',
      done: 'Credit balance is now {{balance}}.',
    },
    payments: {
      title: 'Payments and refunds',
      id: 'Payment',
      refunds: 'Refunds',
      full: 'Full',
      refund: 'Refund',
      refundTitle: 'Refund payment {{id}}',
      refundNote:
        'Card payments only. A pending refund is never sent again; ATM payments are refunded in the Aircraft Pay console.',
      refundAmount: 'Amount (NT$)',
      refundMax: 'At most {{amount}}.',
      refundConfirm: 'Refund {{amount}}',
      refundRequested: 'Refund requested.',
      refundStatus: {
        requested: 'requested',
        pending: 'pending',
        succeeded: 'refunded',
        failed: 'failed',
      },
    },
    flags: {
      title: 'Feature flags',
      description: 'On for everyone, or only for the listed workspaces.',
      new: 'New flag',
      empty: 'No feature flags',
      key: 'Key',
      keyHint: 'Lower case letters, digits, "_" and ".", starting with a letter.',
      keyInvalid: 'Use lower case letters, digits, "_" and ".", starting with a letter.',
      descriptionLabel: 'Description',
      enabled: 'On',
      workspaces: 'Only for these workspaces',
      workspacesHint: 'Workspace IDs, one per line. Empty means every workspace.',
      workspacesInvalid: 'Each line must be a workspace ID (UUID).',
      state: 'State',
      updated: 'Updated',
      onAll: 'On for all',
      onFor_one: 'On for {{count}} workspace',
      onFor_other: 'On for {{count}} workspaces',
      off: 'Off',
      editTitle: 'Edit {{key}}',
      confirmDelete: 'Delete the flag {{key}}?',
    },
    alerts: {
      title: 'Alerts',
      description: 'Payment routing problems, failed refunds and payments Smoke could not match.',
      showResolved: 'Show resolved',
      empty: 'No alerts',
      workspace: 'Workspace',
      resolve: 'Mark resolved',
      resolved: 'Resolved',
      field: {
        accountId: 'Account (billing)',
        orderId: 'Order',
        code: 'Error code',
        status: 'HTTP status',
        paymentId: 'Aircraft Pay payment',
        refundId: 'Refund',
        externalRefundId: 'Aircraft Pay refund',
        failureCode: 'Failure code',
        assetId: 'Asset',
      },
      kind: {
        billing_merchant_not_routed: 'Merchant not routed (422)',
        billing_refund_failed: 'Refund failed',
        billing_payment_unmatched: 'Payment could not be matched',
        billing_duplicate_purchase: 'Duplicate purchase',
        billing_charge_rejected: 'Charge rejected',
        github_scan_failed: 'GitHub scan failed',
      },
    },
    users: {
      title: 'Staff members',
      description:
        'Staff roles are separate from workspace roles. Staff need 2FA to use the console.',
      add: 'Grant a staff role',
      role: 'Role',
      grant: 'Grant',
      twoFactorOn: '2FA on',
      twoFactorOff: '2FA off',
      confirmRemove: 'Remove the staff role of {{email}}?',
    },
  },
};
